import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ChevronLeft,
  Edit,
  Trash2,
  Phone,
  MapPin,
  Calendar,
  AlertTriangle,
  User,
  FileText,
  Activity,
  Stethoscope,
  Send,
  ClipboardList,
  Upload,
  File,
  Download,
  Eye,
  FolderOpen,
  FilePlus,
  PenTool,
  Clock,
  Heart,
  Plus,
  Link,
  ExternalLink,
  Printer,
  Copy,
  CheckCircle,
} from "lucide-react";
import { toast } from "sonner";
import { SignaturePad } from "@/components/SignaturePad";
import ROILaunchDialog from "@/components/ROILaunchDialog";
import ClientSignaturePinCard from "@/components/ClientSignaturePinCard";
import { Check } from "lucide-react";
import { 
  OverviewTab, 
  MedicalTab, 
  NotesTab, 
  CarePlansTab,
  DocumentsTab, 
  CMS485Tab,
  AssessmentsTab,
  AdmissionFormsTab,
  SupervisoryVisitsTab,
  ScheduleTab, 
  VisitNotesTab,
  ServiceOrdersTab,
  ServiceOrderDialog,
  NotesDialog,
  SupervisoryVisitDialog,
  QuarterlyMeetingDialog,
  CarePlanDialog,
  DocumentUploadDialog,
  FaxMDOrderDialog,
  EfaxDialog,
  SignLinkDialog,
  EditMDOrderDialog,
  ViewMDOrderDialog,
  CMS485Dialog,
} from "@/components/client-detail";
import { EMPTY_DISCHARGE } from "@/components/client-detail/DischargeOrderFields";
import { EMPTY_HOLD } from "@/components/client-detail/ServiceHoldFields";
import { ScheduleCalendar } from "@/components/ScheduleCalendar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DIABETIC_MOT = "Assist client with glucose monitoring as directed; record readings and report values outside parameters to RN";

const statusOptions = [
  { value: "potential", label: "Potential" },
  { value: "active", label: "Active" },
  { value: "discharged", label: "Discharged" },
  { value: "on_hold", label: "On Hold" },
  { value: "inactive", label: "Inactive" },
  { value: "non_admit", label: "Non-Admit" },
];

export default function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [faxDialogOpen, setFaxDialogOpen] = useState(false);
  const [roiDialogOpen, setRoiDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "overview");
  const [docsCategoryFilter, setDocsCategoryFilter] = useState("all");
  const [faxOrderTypes, setFaxOrderTypes] = useState({
    personal_care: false,
    dme_supplies: false,
    service_hold: false,
    discharge: false,
  });
  const [faxNotes, setFaxNotes] = useState("");
  const [faxDischarge, setFaxDischarge] = useState(EMPTY_DISCHARGE);
  const [faxHold, setFaxHold] = useState(EMPTY_HOLD);
  const [faxDme, setFaxDme] = useState({ dme_other: "", patient_height: "", patient_weight: "" });

  const openDmeOrderFromNeeded = () => {
    setFaxOrderTypes({ personal_care: false, dme_supplies: true, service_hold: false, discharge: false });
    setFaxDme({
      dme_other: client?.dme_needed || "",
      patient_height: client?.height || "",
      patient_weight: client?.weight || "",
    });
    setFaxDialogOpen(true);
  };
  const [sendingFax, setSendingFax] = useState(false);
  const [mdOrders, setMdOrders] = useState([]);
  const [editMdOrderDialogOpen, setEditMdOrderDialogOpen] = useState(false);
  const [editingMdOrder, setEditingMdOrder] = useState(null);
  const [viewMdOrderDialogOpen, setViewMdOrderDialogOpen] = useState(false);
  const [viewingMdOrder, setViewingMdOrder] = useState(null);
  const [carePlans, setCarePlans] = useState([]);
  const [carePlanDialogOpen, setCarePlanDialogOpen] = useState(false);
  const [newCarePlan, setNewCarePlan] = useState({
    title: "",
    goals: "",
    interventions: "",
    frequency: "",
  });
  const [savingCarePlan, setSavingCarePlan] = useState(false);
  
  // Physicians for dropdown selection
  const [physicians, setPhysicians] = useState([]);
  
  // RN & Office Notes state
  const [rnNotes, setRnNotes] = useState([]);
  const [officeNotes, setOfficeNotes] = useState([]);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [noteType, setNoteType] = useState("rn");
  const [newNote, setNewNote] = useState("");
  const [newNoteDate, setNewNoteDate] = useState(new Date().toISOString().split('T')[0]);
  const [savingNote, setSavingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  
  // Supervisory Visits state
  const [supervisoryVisits, setSupervisoryVisits] = useState([]);
  const [supervisoryDialogOpen, setSupervisoryDialogOpen] = useState(false);
  const [savingSupervisory, setSavingSupervisory] = useState(false);
  
  // Helper to calculate next visit date (55 days after initial visit)
  const calculateNextVisitDate = (visitDate) => {
    if (!visitDate) return "";
    const date = new Date(visitDate);
    date.setDate(date.getDate() + 55);
    return date.toISOString().split('T')[0];
  };
  
  const initialVisitDate = new Date().toISOString().split('T')[0];
  const [newSupervisoryVisit, setNewSupervisoryVisit] = useState({
    visit_date: initialVisitDate,
    visit_completed: true,
    next_visit_date: calculateNextVisitDate(initialVisitDate),
    next_visit_time: "10:00",
    supervisor_name: "",
    notes: "",
  });
  const [supervisoryFormFile, setSupervisoryFormFile] = useState(null);
  
  // Handler to update visit date and auto-set next visit date
  const handleVisitDateChange = (newDate) => {
    setNewSupervisoryVisit(prev => ({
      ...prev,
      visit_date: newDate,
      next_visit_date: calculateNextVisitDate(newDate)
    }));
  };
  
  // Client Schedule state
  const [clientSchedule, setClientSchedule] = useState({
    appointments: [],
    assigned_caregivers: [],
    total_scheduled: 0,
    total_completed: 0
  });
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  
  // Quarterly Meetings state (Admin only)
  const [quarterlyMeetings, setQuarterlyMeetings] = useState([]);
  const [quarterlyDialogOpen, setQuarterlyDialogOpen] = useState(false);
  const [savingQuarterly, setSavingQuarterly] = useState(false);
  const [newQuarterlyMeeting, setNewQuarterlyMeeting] = useState({
    meeting_date: "",
    meeting_time: "10:00",
    notes: "",
    next_meeting_date: ""
  });
  const [quarterlyFile, setQuarterlyFile] = useState(null);
  const [uploadingQuarterlyDoc, setUploadingQuarterlyDoc] = useState(false);
  const quarterlyFileRef = useRef(null);
  
  // Care Plan POC (Plan of Care - PCW/Caregiver) state
  const [carePlanPOCs, setCarePlanPOCs] = useState([]);
  const [carePlanPOCDialogOpen, setCarePlanPOCDialogOpen] = useState(false);
  const [editingPocId, setEditingPocId] = useState(null); // For edit mode
  const [savingCarePlanPOC, setSavingCarePlanPOC] = useState(false);
  const [pocSignatureDialogOpen, setPocSignatureDialogOpen] = useState(false);
  const [signingPocId, setSigningPocId] = useState(null);
  const [pocSignerType, setPocSignerType] = useState("rn");
  const [pocSignerName, setPocSignerName] = useState("");
  // RN Review state
  const [rnReviewDialogOpen, setRnReviewDialogOpen] = useState(false);
  const [reviewingPocId, setReviewingPocId] = useState(null);
  const [rnReviewNotes, setRnReviewNotes] = useState("");
  const [rnReviewSignature, setRnReviewSignature] = useState(null);
  const [savingRnReview, setSavingRnReview] = useState(false);
  const [viewReviewsDialogOpen, setViewReviewsDialogOpen] = useState(false);
  const [viewingPoc, setViewingPoc] = useState(null);
  const daysOfWeek = ["M", "T", "W", "Th", "F", "S", "Su"];
  const assistanceTypes = ["Independent", "Supervision", "Partial Assist", "Total Assist", "N/A"];
  const mealsAssistanceTypes = ["Self", "Set up", "Supervision", "Total Care"];
  const [newCarePlanPOC, setNewCarePlanPOC] = useState({
    preferred_name: "",
    ability_to_sign: "Yes",
    pets: "",
    emergency_contact_name: "",
    emergency_contact_relationship: "",
    emergency_contact_phone: "",
    emergency_contact_alt_phone: "",
    family_support: "",
    chronic_health_problems: "",
    sleep_habits: "",
    precautions: "",
    dme: "",
    hearing_vision_speech: "",
    mental_status: "",
    allergies: "",
    code_status: "Full Code",
    hours_per_week: "",
    days_of_week: [],
    time_of_visits: "",
    soc_date: new Date().toISOString().split('T')[0],
    adl_bathing: { assistance_type: "", frequency: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
    adl_dressing: { assistance_type: "", frequency: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
    adl_grooming: { assistance_type: "", frequency: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
    adl_meals: { assistance_type: "", frequency: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
    adl_mobility: { assistance_type: "", frequency: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
    adl_toileting: { assistance_type: "", frequency: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
    adl_transfers: { assistance_type: "", frequency: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
    adl_med_reminder: { assistance_type: "", frequency: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
    adl_cleaning: { assistance_type: "", frequency: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
    adl_laundry: { assistance_type: "", frequency: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
    adl_other: { assistance_type: "", frequency: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
    other_adl_description: "",
    parameters: "",
    interventions: "",
    goals: "",
  });
  
  // Document upload state
  const [documents, setDocuments] = useState([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [documentType, setDocumentType] = useState("other");
  const [documentNotes, setDocumentNotes] = useState("");
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  
  // eFax Document Sending state
  const [efaxDialogOpen, setEfaxDialogOpen] = useState(false);
  const [faxOrders, setFaxOrders] = useState([]);
  const [sendingEfax, setSendingEfax] = useState(false);
  const [efaxData, setEfaxData] = useState({
    recipient_fax: "",
    recipient_name: "",
    document_type: "cms485",
    document_id: "",
    notes: "",
    urgent: false,
  });
  
  // Physician Sign Link state
  const [signLinkDialogOpen, setSignLinkDialogOpen] = useState(false);
  const [signLinks, setSignLinks] = useState([]);
  const [creatingSignLink, setCreatingSignLink] = useState(false);
  const [createdSignLinkUrl, setCreatedSignLinkUrl] = useState(null);
  const [createdSignLinkMeta, setCreatedSignLinkMeta] = useState(null);
  const [signLinkData, setSignLinkData] = useState({
    document_type: "cms485",
    document_id: "",
    physician_name: "",
    physician_email: "",
    expires_days: 7
  });
  
  // CMS 485 state
  const [cms485Forms, setCms485Forms] = useState([]);
  const [cms485DialogOpen, setCms485DialogOpen] = useState(false);
  const [savingCms485, setSavingCms485] = useState(false);
  const [editingCms485Id, setEditingCms485Id] = useState(null);
  const [clearCms485Signatures, setClearCms485Signatures] = useState(false);
  const [rnEmployees, setRnEmployees] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [cms485Form, setCms485Form] = useState({
    certification_period_from: "",
    certification_period_to: "",
    principal_diagnosis: "",
    secondary_diagnosis: "",
    surgical_procedures: "",
    other_diagnoses: "",
    dme_supplies: "",
    safety_measures: "",
    nutritional_requirements: "",
    allergies: "",
    functional_limitations: [],
    activities_permitted: [],
    mental_status: [],
    prognosis: "good",
    orders_for_discipline: "",
    goals: "",
    discharge_plans: "",
    physician_id: "",
    physician_name: "",
    physician_phone: "",
    physician_address: "",
    agency_rn_id: "",
    agency_rn_name: "",
    // PCW Service Fields
    pcw_hours_per_day: "",
    pcw_days_per_week: "",
    pcw_weeks: "",
    pcw_poc_days_per_week: "",
    pcw_prn_hours: false,
    pcw_tasks: {
      bathing: { assistance: "", frequency: "" },
      dressing_upper_body: { assistance: "", frequency: "" },
      dressing_lower_body: { assistance: "", frequency: "" },
      grooming: { assistance: "", frequency: "" },
      eating: { assistance: "", frequency: "" },
      mobility_in_home: { assistance: "", frequency: "" },
      toileting: { assistance: "", frequency: "" },
      transfers: { assistance: "", frequency: "" },
    },
    pcw_mots: "",
    pcw_interventions_parameters: "",
  });

  const pcwAssistanceLevels = [
    { value: "none", label: "No assistance needed" },
    { value: "supervision", label: "Supervision/Cueing" },
    { value: "partial", label: "Partial Assistance" },
    { value: "total", label: "Total Assistance needed" },
  ];

  const pcwFrequencyOptions = [
    { value: "1", label: "1 time per day" },
    { value: "2", label: "2 times per day" },
    { value: "3", label: "3 times per day" },
    { value: "4", label: "4 times per day" },
  ];

  const pcwTaskLabels = {
    bathing: "Bathing",
    dressing_upper_body: "Dressing Upper Body",
    dressing_lower_body: "Dressing Lower Body",
    grooming: "Grooming",
    eating: "Eating",
    mobility_in_home: "Mobility in Home",
    toileting: "Toileting",
    transfers: "Transfers",
  };

  const documentTypes = [
    { value: "cms_485", label: "CMS 485 Form" },
    { value: "physician_order", label: "Physician Order" },
    { value: "assessment", label: "Assessment" },
    { value: "care_plan", label: "Care Plan" },
    { value: "progress_note", label: "Progress Note" },
    { value: "insurance", label: "Insurance Document" },
    { value: "consent", label: "Consent Form" },
    { value: "lab_result", label: "Lab Result" },
    { value: "other", label: "Other" },
  ];

  const functionalLimitOptions = [
    "Ambulation", "Bowel/Bladder", "Contracture", "Hearing", "Paralysis", 
    "Endurance", "Speech", "Legally Blind", "Dyspnea with Minimal Exertion", "Other"
  ];

  const activitiesPermittedOptions = [
    "Bedrest", "BRP", "Up as Tolerated", "Transfer Bed/Chair", "Exercises Prescribed",
    "Partial Weight Bearing", "Independent at Home", "Crutches", "Cane", "Wheelchair", "Walker", "Other"
  ];

  const mentalStatusOptions = [
    "Oriented", "Comatose", "Forgetful", "Depressed", "Disoriented", "Lethargic", "Agitated", "Other"
  ];

  // Visit Notes state
  const [visitNotes, setVisitNotes] = useState([]);
  const [visitNoteDialogOpen, setVisitNoteDialogOpen] = useState(false);
  const [savingVisitNote, setSavingVisitNote] = useState(false);
  const [newVisitNote, setNewVisitNote] = useState({
    visit_date: new Date().toISOString().split('T')[0],
    time_in: "",
    time_out: "",
    visit_type: "routine",
    duration_minutes: 60,
    services_provided: [],
    vital_signs: { blood_pressure: "", heart_rate: "", temperature: "", oxygen_saturation: "" },
    pain_level: null,
    mots_observations: "",
    patient_condition: "stable",
    patient_response: "",
    follow_up_needed: false,
    follow_up_notes: "",
    caregiver_signature: null,
    client_signature: null,
  });

  // CMS 485 signature state
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [signingFormId, setSigningFormId] = useState(null);
  const [signerType, setSignerType] = useState("agency");
  const [signerName, setSignerName] = useState("");

  const visitTypes = [
    { value: "routine", label: "Routine Visit" },
    { value: "assessment", label: "Assessment" },
    { value: "admission", label: "Admission" },
    { value: "discharge", label: "Discharge" },
    { value: "follow_up", label: "Follow Up" },
  ];

  const servicesOptions = [
    "Personal Care", "Bathing", "Dressing", "Grooming", "Toileting",
    "Medication Reminder", "Vital Signs", "Range of Motion", "Ambulation",
    "Meal Preparation", "Light Housekeeping", "Companionship", "Transportation"
  ];

  // Service Orders state
  const [serviceOrders, setServiceOrders] = useState([]);
  const [serviceOrderDialogOpen, setServiceOrderDialogOpen] = useState(false);
  const [savingServiceOrder, setSavingServiceOrder] = useState(false);
  const [editingServiceOrderId, setEditingServiceOrderId] = useState(null);
  const [masterServiceCodes, setMasterServiceCodes] = useState([]);
  const [newServiceOrder, setNewServiceOrder] = useState({
    service_code: "",
    service_type: "",
    payer_type: "",
    payer_name: "",
    authorization_number: "",
    patient_number: "",
    diagnosis_code: "",
    start_date: "",
    end_date: "",
    start_time: "",
    end_time: "",
    flexible_time: false,
    flexible_days: false,
    recur_every: 1,
    selected_days: [],
    units_per_hour: "",
    cost_per_hour: "",
    daily_hours: "",
    hours_per_week: "",
    hours_per_day: "",
    total_authorized_hours: "",
    total_approved_units: "",
    total_approved_amount: "",
    total_approved_visits: "",
    visits_per_week: "",
    units_per_service: "",
    billing_rate: "",
    payroll_rate: "",
    caregiver_id: "",
    caregiver_name: "",
    payroll_item: "",
    set_as_default: false,
    include_service_modifiers: false,
    auto_create_schedules: false,
    regenerate_schedules: false,
    notes: "",
    status: "active",
    _caregiverHasPayrollRate: null
  });

  const serviceTypeOptions = [
    { value: "personal_care", label: "Personal Care" },
    { value: "skilled_nursing", label: "Skilled Nursing" },
    { value: "companion_care", label: "Companion Care" },
    { value: "respite_care", label: "Respite Care" },
    { value: "home_health_aide", label: "Home Health Aide" },
  ];

  const payerTypeOptions = [
    { value: "medicaid", label: "Medicaid" },
    { value: "bcbs", label: "BCBS" },
    { value: "include", label: "Include" },
    { value: "molina", label: "Molina" },
    { value: "uhc", label: "UHC" },
    { value: "mhs", label: "MHS" },
    { value: "community_care", label: "Community Care" },
    { value: "family_care", label: "Family Care" },
    { value: "iris", label: "IRIS" },
  ];

  // Auto-calculate service order totals
  const calculateServiceOrderTotals = (field, value, currentOrder) => {
    const updated = { ...currentOrder, [field]: value };
    
    const unitsPerHour = parseFloat(updated.units_per_hour) || 0;
    const costPerHour = parseFloat(updated.cost_per_hour) || 0;
    const hoursPerWeek = parseFloat(updated.hours_per_week) || 0;
    const hoursPerDay = parseFloat(updated.hours_per_day) || 0;
    const startDate = updated.start_date;
    const endDate = updated.end_date;
    
    // Auto-calculate total authorized hours from hours_per_week and date range
    if (startDate && endDate && (hoursPerWeek > 0 || hoursPerDay > 0)) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffTime = end.getTime() - start.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // Include both start and end days
      const diffWeeks = diffDays / 7;
      
      if (hoursPerWeek > 0) {
        // Calculate based on hours per week
        updated.total_authorized_hours = (hoursPerWeek * diffWeeks).toFixed(2);
      } else if (hoursPerDay > 0) {
        // Calculate based on hours per day (assume 5 working days per week)
        const workingDays = Math.ceil(diffDays * (5 / 7)); // Approximate working days
        updated.total_authorized_hours = (hoursPerDay * workingDays).toFixed(2);
      }
    }
    
    const totalAuthHours = parseFloat(updated.total_authorized_hours) || 0;
    
    // Calculate total approved units = total authorized hours * units per hour
    if (unitsPerHour > 0 && totalAuthHours > 0) {
      updated.total_approved_units = (totalAuthHours * unitsPerHour).toFixed(2);
    }
    
    // Calculate total approved amount = total authorized hours * cost per hour
    if (costPerHour > 0 && totalAuthHours > 0) {
      updated.total_approved_amount = (totalAuthHours * costPerHour).toFixed(2);
    }
    
    return updated;
  };

  useEffect(() => {
    fetchClient();
    fetchMdOrders();
    fetchCarePlans();
    fetchCarePlanPOCs();
    fetchDocuments();
    fetchCms485Forms();
    fetchVisitNotes();
    fetchFaxOrders();
    fetchPhysicians();
    fetchClientNotes();
    fetchSupervisoryVisits();
    fetchClientSchedule();
    fetchQuarterlyMeetings();
    fetchServiceOrders();
    fetchMasterServiceCodes();
    fetchRnEmployees();
    fetchAllEmployees();
  }, [id]);

  const fetchAllEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error("Failed to fetch employees:", error);
    }
  };

  const fetchRnEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees?role=rn`);
      setRnEmployees(response.data);
    } catch (error) {
      console.error("Failed to fetch RN employees:", error);
    }
  };

  // Fetch sign links when CMS485 forms or POCs change
  useEffect(() => {
    if (cms485Forms.length > 0 || carePlanPOCs.length > 0) {
      fetchSignLinks();
    }
  }, [cms485Forms, carePlanPOCs]);

  const fetchClient = async () => {
    try {
      const response = await axios.get(`${API}/clients/${id}`);
      setClient(response.data);
    } catch (error) {
      console.error("Failed to fetch client:", error);
      toast.error("Failed to load client details");
      navigate("/clients");
    } finally {
      setLoading(false);
    }
  };

  const fetchSupervisoryVisits = async () => {
    try {
      const response = await axios.get(`${API}/supervisory-visits?client_id=${id}`);
      setSupervisoryVisits(response.data);
    } catch (error) {
      console.error("Failed to fetch supervisory visits:", error);
    }
  };

  const fetchClientSchedule = async () => {
    setLoadingSchedule(true);
    try {
      const response = await axios.get(`${API}/clients/${id}/schedule`);
      setClientSchedule(response.data);
    } catch (error) {
      console.error("Failed to fetch client schedule:", error);
    } finally {
      setLoadingSchedule(false);
    }
  };

  const fetchQuarterlyMeetings = async () => {
    if (user?.role !== "admin") return;
    try {
      const response = await axios.get(`${API}/clients/${id}/quarterly-meetings`);
      setQuarterlyMeetings(response.data);
    } catch (error) {
      console.error("Failed to fetch quarterly meetings:", error);
    }
  };

  const handleCreateQuarterlyMeeting = async () => {
    if (!newQuarterlyMeeting.meeting_date) {
      toast.error("Please select a meeting date");
      return;
    }

    setSavingQuarterly(true);
    try {
      await axios.post(`${API}/clients/${id}/quarterly-meetings`, {
        client_id: id,
        ...newQuarterlyMeeting
      });
      toast.success("Quarterly meeting scheduled!");
      setQuarterlyDialogOpen(false);
      setNewQuarterlyMeeting({
        meeting_date: "",
        meeting_time: "10:00",
        notes: "",
        next_meeting_date: ""
      });
      fetchQuarterlyMeetings();
    } catch (error) {
      console.error("Failed to create quarterly meeting:", error);
      toast.error("Failed to schedule meeting");
    } finally {
      setSavingQuarterly(false);
    }
  };

  const handleUploadQuarterlyDoc = async (meetingId) => {
    if (!quarterlyFile) {
      toast.error("Please select a file");
      return;
    }

    setUploadingQuarterlyDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', quarterlyFile);

      await axios.post(`${API}/quarterly-meetings/${meetingId}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      toast.success("Document uploaded!");
      setQuarterlyFile(null);
      if (quarterlyFileRef.current) quarterlyFileRef.current.value = "";
      fetchQuarterlyMeetings();
    } catch (error) {
      console.error("Failed to upload document:", error);
      toast.error("Failed to upload document");
    } finally {
      setUploadingQuarterlyDoc(false);
    }
  };

  const handleDeleteQuarterlyMeeting = async (meetingId) => {
    try {
      await axios.delete(`${API}/quarterly-meetings/${meetingId}`);
      toast.success("Meeting deleted");
      fetchQuarterlyMeetings();
    } catch (error) {
      console.error("Failed to delete meeting:", error);
      toast.error("Failed to delete meeting");
    }
  };

  const handleDeleteQuarterlyDoc = async (meetingId, docId) => {
    try {
      await axios.delete(`${API}/quarterly-meetings/${meetingId}/documents/${docId}`);
      toast.success("Document deleted");
      fetchQuarterlyMeetings();
    } catch (error) {
      console.error("Failed to delete document:", error);
      toast.error("Failed to delete document");
    }
  };

  const handleUpdateNextMeetingDate = async (meetingId, nextDate) => {
    try {
      await axios.patch(`${API}/quarterly-meetings/${meetingId}`, {
        next_meeting_date: nextDate
      });
      toast.success("Next meeting date updated!");
      fetchQuarterlyMeetings();
    } catch (error) {
      console.error("Failed to update next meeting date:", error);
      toast.error("Failed to update");
    }
  };

  const handleCreateSupervisoryVisit = async () => {
    if (!newSupervisoryVisit.visit_date) {
      toast.error("Please enter visit date");
      return;
    }
    
    setSavingSupervisory(true);
    try {
      const response = await axios.post(`${API}/supervisory-visits`, {
        client_id: id,
        visit_date: newSupervisoryVisit.visit_date,
        visit_completed: newSupervisoryVisit.visit_completed,
        next_visit_date: newSupervisoryVisit.next_visit_date || null,
        next_visit_time: newSupervisoryVisit.next_visit_time || null,
        supervisor_name: user?.name || newSupervisoryVisit.supervisor_name,
        notes: newSupervisoryVisit.notes,
      });
      
      // If there's a form file, upload it
      if (supervisoryFormFile) {
        const formData = new FormData();
        formData.append('file', supervisoryFormFile);
        await axios.post(`${API}/supervisory-visits/${response.data.id}/upload-form`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      toast.success("Supervisory visit recorded");
      setSupervisoryDialogOpen(false);
      const resetDate = new Date().toISOString().split('T')[0];
      setNewSupervisoryVisit({
        visit_date: resetDate,
        visit_completed: true,
        next_visit_date: calculateNextVisitDate(resetDate),
        next_visit_time: "10:00",
        supervisor_name: "",
        notes: "",
      });
      setSupervisoryFormFile(null);
      fetchSupervisoryVisits();
    } catch (error) {
      console.error("Failed to create supervisory visit:", error);
      toast.error("Failed to record supervisory visit");
    } finally {
      setSavingSupervisory(false);
    }
  };

  const handleDeleteSupervisoryVisit = async (visitId) => {
    try {
      await axios.delete(`${API}/supervisory-visits/${visitId}`);
      toast.success("Supervisory visit deleted");
      fetchSupervisoryVisits();
    } catch (error) {
      console.error("Failed to delete supervisory visit:", error);
      toast.error("Failed to delete visit");
    }
  };

  // Service Orders functions
  const fetchServiceOrders = async () => {
    try {
      const response = await axios.get(`${API}/clients/${id}/service-orders`);
      setServiceOrders(response.data);
    } catch (error) {
      console.error("Failed to fetch service orders:", error);
    }
  };

  const fetchMasterServiceCodes = async () => {
    try {
      const response = await axios.get(`${API}/master-service-codes`);
      setMasterServiceCodes(response.data);
    } catch (error) {
      console.error("Failed to fetch master service codes:", error);
    }
  };

  const handleServiceCodeChange = (code) => {
    const masterCode = masterServiceCodes.find(mc => mc.code === code);
    if (masterCode) {
      // Map service code to payroll item
      const payrollItemMap = {
        'T1019': 'hourly_personal_care',
        'T1020': 'hourly_personal_care',
        'T1021': 'hourly_personal_care',
        'S5125': 'hourly_supportive_care',
        'S5126': 'hourly_supportive_care',
        'S5130': 'hourly_supportive_care',
        'S5131': 'hourly_supportive_care',
        'S5135': 'hourly_supportive_care',
        'S5136': 'hourly_supportive_care',
        'G0156': 'hourly_personal_care',
      };
      
      // Check if modifier is travel time
      const isTravel = masterCode.modifier === 'U3' || masterCode.description?.toLowerCase().includes('travel');
      const payrollItem = isTravel ? 'hourly_travel_time' : (payrollItemMap[code] || 'hourly_personal_care');
      
      // Auto-populate values from master service code
      setNewServiceOrder(prev => calculateServiceOrderTotals('service_code', code, {
        ...prev,
        service_code: code,
        units_per_hour: masterCode.units_per_hour || prev.units_per_hour,
        cost_per_hour: masterCode.cost_per_hour || prev.cost_per_hour,
        payer_type: masterCode.default_payer_type || prev.payer_type,
        // Auto-fill scheduling fields
        payroll_item: payrollItem,
        payroll_rate: masterCode.cost_per_hour?.toString() || prev.payroll_rate,
        billing_rate: masterCode.reimbursement_amount?.toString() || prev.billing_rate,
      }));
    } else {
      setNewServiceOrder(prev => ({ ...prev, service_code: code }));
    }
  };

  const handleCreateServiceOrder = async () => {
    if (!newServiceOrder.service_type || !newServiceOrder.payer_type || !newServiceOrder.start_date || !newServiceOrder.end_date) {
      toast.error("Please fill in required fields: Service Type, Payer Type, Start Date, End Date");
      return;
    }

    // Validate time format if auto_create_schedules is enabled
    if (newServiceOrder.auto_create_schedules) {
      const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
      if (!newServiceOrder.start_time || !timeRegex.test(newServiceOrder.start_time)) {
        toast.error("Please enter a valid Start Time in 24hr format (e.g., 14:30) for schedule creation");
        return;
      }
      if (!newServiceOrder.end_time || !timeRegex.test(newServiceOrder.end_time)) {
        toast.error("Please enter a valid End Time in 24hr format (e.g., 18:30) for schedule creation");
        return;
      }
      if (!newServiceOrder.selected_days || newServiceOrder.selected_days.length === 0) {
        toast.error("Please select at least one day for schedule creation");
        return;
      }
    }

    setSavingServiceOrder(true);
    try {
      const orderData = {
        ...newServiceOrder,
        client_id: id,
        units_per_hour: newServiceOrder.units_per_hour ? parseFloat(newServiceOrder.units_per_hour) : null,
        cost_per_hour: newServiceOrder.cost_per_hour ? parseFloat(newServiceOrder.cost_per_hour) : null,
        daily_hours: newServiceOrder.daily_hours ? parseFloat(newServiceOrder.daily_hours) : null,
        hours_per_week: newServiceOrder.hours_per_week ? parseFloat(newServiceOrder.hours_per_week) : null,
        hours_per_day: newServiceOrder.hours_per_day ? parseFloat(newServiceOrder.hours_per_day) : null,
        total_authorized_hours: newServiceOrder.total_authorized_hours ? parseFloat(newServiceOrder.total_authorized_hours) : null,
        total_approved_units: newServiceOrder.total_approved_units ? parseFloat(newServiceOrder.total_approved_units) : null,
        total_approved_amount: newServiceOrder.total_approved_amount ? parseFloat(newServiceOrder.total_approved_amount) : null,
        total_approved_visits: newServiceOrder.total_approved_visits ? parseInt(newServiceOrder.total_approved_visits) : null,
        visits_per_week: newServiceOrder.visits_per_week ? parseInt(newServiceOrder.visits_per_week) : null,
        units_per_service: newServiceOrder.units_per_service ? parseFloat(newServiceOrder.units_per_service) : null,
        billing_rate: newServiceOrder.billing_rate ? parseFloat(newServiceOrder.billing_rate) : null,
        payroll_rate: newServiceOrder.payroll_rate ? parseFloat(newServiceOrder.payroll_rate) : null,
        recur_every: parseInt(newServiceOrder.recur_every) || 1,
      };

      // Debug log to verify times are being sent
      console.log('Authorization data being sent:', {
        start_time: orderData.start_time,
        end_time: orderData.end_time,
        selected_days: orderData.selected_days,
        auto_create_schedules: orderData.auto_create_schedules
      });

      if (editingServiceOrderId) {
        const response = await axios.patch(`${API}/service-orders/${editingServiceOrderId}`, orderData);
        console.log('Update response:', response.data);
        if (response.data.appointments_regenerated) {
          toast.success(`Authorization updated. ${response.data.new_appointments_created} appointments regenerated.`);
        } else {
          toast.success("Authorization updated");
        }
      } else {
        const response = await axios.post(`${API}/clients/${id}/service-orders`, orderData);
        console.log('Create response:', response.data);
        if (response.data.appointments_created > 0) {
          toast.success(`Authorization created with ${response.data.appointments_created} appointments scheduled at ${orderData.start_time}`);
        } else {
          toast.success("Authorization created");
        }
      }

      setServiceOrderDialogOpen(false);
      resetServiceOrderForm();
      fetchServiceOrders();
      // Refresh the schedule to show new appointments
      fetchClientSchedule();
    } catch (error) {
      console.error("Failed to save authorization:", error);
      toast.error(error.response?.data?.detail || "Failed to save authorization");
    } finally {
      setSavingServiceOrder(false);
    }
  };

  const handleEditServiceOrder = (order) => {
    setEditingServiceOrderId(order.id);
    setNewServiceOrder({
      service_code: order.service_code || "",
      service_type: order.service_type || "",
      payer_type: order.payer_type || "",
      payer_name: order.payer_name || "",
      authorization_number: order.authorization_number || "",
      patient_number: order.patient_number || "",
      diagnosis_code: order.diagnosis_code || "",
      start_date: order.start_date || "",
      end_date: order.end_date || "",
      start_time: order.start_time || "",
      end_time: order.end_time || "",
      flexible_time: order.flexible_time || false,
      flexible_days: order.flexible_days || false,
      recur_every: order.recur_every || 1,
      selected_days: order.selected_days || [],
      units_per_hour: order.units_per_hour?.toString() || "",
      cost_per_hour: order.cost_per_hour?.toString() || "",
      daily_hours: order.daily_hours?.toString() || "",
      hours_per_week: order.hours_per_week?.toString() || "",
      hours_per_day: order.hours_per_day?.toString() || "",
      total_authorized_hours: order.total_authorized_hours?.toString() || "",
      total_approved_units: order.total_approved_units?.toString() || "",
      total_approved_amount: order.total_approved_amount?.toString() || "",
      total_approved_visits: order.total_approved_visits?.toString() || "",
      visits_per_week: order.visits_per_week?.toString() || "",
      units_per_service: order.units_per_service?.toString() || "",
      billing_rate: order.billing_rate?.toString() || "",
      payroll_rate: order.payroll_rate?.toString() || "",
      caregiver_id: order.caregiver_id || "",
      caregiver_name: order.caregiver_name || "",
      payroll_item: order.payroll_item || "",
      set_as_default: order.set_as_default || false,
      include_service_modifiers: order.include_service_modifiers || false,
      auto_create_schedules: order.auto_create_schedules || false,
      regenerate_schedules: false,
      notes: order.notes || "",
      status: order.status || "active",
      _caregiverHasPayrollRate: null,
    });
    setServiceOrderDialogOpen(true);
  };

  const handleDeleteServiceOrder = async (orderId) => {
    try {
      const response = await axios.delete(`${API}/service-orders/${orderId}`);
      toast.success(response.data.message || "Authorization deleted");
      fetchServiceOrders();
      // Also refresh the schedule to remove deleted appointments
      fetchClientSchedule();
    } catch (error) {
      console.error("Failed to delete authorization:", error);
      toast.error("Failed to delete authorization");
    }
  };

  const handleClearOrphanedSchedules = async () => {
    try {
      const response = await axios.delete(`${API}/service-orders/client/${id}/clear-schedules`);
      toast.success(response.data.message || "Schedules cleared");
      // Refresh appointments
      fetchClientSchedule();
    } catch (error) {
      console.error("Failed to clear schedules:", error);
      toast.error("Failed to clear schedules");
    }
  };

  const resetServiceOrderForm = () => {
    setEditingServiceOrderId(null);
    setNewServiceOrder({
      service_code: "",
      service_type: "",
      payer_type: "",
      payer_name: "",
      authorization_number: "",
      patient_number: "",
      diagnosis_code: "",
      start_date: "",
      end_date: "",
      start_time: "",
      end_time: "",
      flexible_time: false,
      flexible_days: false,
      recur_every: 1,
      selected_days: [],
      units_per_hour: "",
      cost_per_hour: "",
      daily_hours: "",
      hours_per_week: "",
      hours_per_day: "",
      total_authorized_hours: "",
      total_approved_units: "",
      total_approved_amount: "",
      total_approved_visits: "",
      visits_per_week: "",
      units_per_service: "",
      billing_rate: "",
      payroll_rate: "",
      caregiver_id: "",
      caregiver_name: "",
      payroll_item: "",
      set_as_default: false,
      include_service_modifiers: false,
      auto_create_schedules: false,
      regenerate_schedules: false,
      notes: "",
      status: "active",
      _caregiverHasPayrollRate: null,
    });
  };

  const getServiceOrderStatusColor = (status) => {
    switch (status) {
      case "active": return "bg-emerald-100 text-emerald-700";
      case "expired": return "bg-slate-100 text-slate-600";
      case "suspended": return "bg-amber-100 text-amber-700";
      case "cancelled": return "bg-red-100 text-red-700";
      default: return "bg-slate-100 text-slate-600";
    }
  };

  const getServiceTypeLabel = (type) => {
    const found = serviceTypeOptions.find(opt => opt.value === type);
    return found ? found.label : type;
  };

  const getPayerTypeLabel = (type) => {
    const found = payerTypeOptions.find(opt => opt.value === type);
    return found ? found.label : type;
  };

  const fetchPhysicians = async () => {
    try {
      const response = await axios.get(`${API}/physicians`);
      setPhysicians(response.data);
    } catch (error) {
      console.error("Failed to fetch physicians:", error);
    }
  };

  const fetchClientNotes = async () => {
    try {
      const response = await axios.get(`${API}/clients/${id}/notes`);
      setRnNotes(response.data.rn_notes || []);
      setOfficeNotes(response.data.office_notes || []);
    } catch (error) {
      console.error("Failed to fetch client notes:", error);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error("Please enter a note");
      return;
    }
    setSavingNote(true);
    try {
      await axios.post(`${API}/clients/${id}/notes`, {
        note_type: noteType,
        note: newNote,
        date: newNoteDate,
        author: user?.name || "Unknown"
      });
      toast.success(`${noteType === "rn" ? "RN" : "Office"} note added`);
      setNotesDialogOpen(false);
      setNewNote("");
      setNewNoteDate(new Date().toISOString().split('T')[0]);
      setEditingNoteId(null);
      fetchClientNotes();
    } catch (error) {
      console.error("Failed to add note:", error);
      toast.error("Failed to add note");
    } finally {
      setSavingNote(false);
    }
  };

  const handlePrintNote = (note, type) => {
    const printContent = `
      <html>
        <head>
          <title>${type === "rn" ? "RN" : "Office"} Note - ${client.first_name} ${client.last_name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; }
            .header { border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 20px; }
            .client-name { font-size: 24px; font-weight: bold; }
            .note-type { font-size: 18px; color: #666; margin-top: 5px; }
            .meta { color: #666; margin: 15px 0; font-size: 14px; }
            .note-content { margin-top: 30px; line-height: 1.8; font-size: 14px; white-space: pre-wrap; border: 1px solid #ddd; padding: 20px; border-radius: 8px; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="client-name">${client.first_name} ${client.last_name}</div>
            <div class="note-type">${type === "rn" ? "RN Note" : "Office Note"}</div>
            <div class="meta">
              Author: ${note.author || "Unknown"} | 
              Date: ${note.date?.split('T')[0] || "N/A"}
            </div>
          </div>
          <div class="note-content">${note.note}</div>
        </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const handleDownloadNote = async (note, type) => {
    try {
      const response = await axios.get(`${API}/clients/${id}/notes/${note.id}/pdf?note_type=${type}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${type === "rn" ? "RN" : "Office"}_Note_${client.first_name}_${client.last_name}_${note.date?.split('T')[0] || 'note'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download note:", error);
      toast.error("Failed to download note");
    }
  };

  const handleDownloadAllNotes = async () => {
    try {
      const response = await axios.get(`${API}/clients/${id}/notes/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `All_Notes_${client.first_name}_${client.last_name}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download notes:", error);
      toast.error("Failed to download notes");
    }
  };

  const handleDeleteNote = async (noteId, type) => {
    try {
      await axios.delete(`${API}/clients/${id}/notes/${noteId}?note_type=${type}`);
      toast.success("Note deleted");
      fetchClientNotes();
    } catch (error) {
      console.error("Failed to delete note:", error);
      toast.error("Failed to delete note");
    }
  };

  const handleEditNote = async (noteId, newText, type) => {
    try {
      await axios.patch(`${API}/clients/${id}/notes/${noteId}`, {
        note: newText,
        note_type: type,
      });
      toast.success("Note updated");
      fetchClientNotes();
    } catch (error) {
      console.error("Failed to edit note:", error);
      toast.error(error.response?.data?.detail || "Failed to update note");
      throw error;
    }
  };

  const fetchMdOrders = async () => {
    try {
      const response = await axios.get(`${API}/md-orders?client_id=${id}`);
      setMdOrders(response.data);
    } catch (error) {
      console.error("Failed to fetch MD orders:", error);
    }
  };

  const fetchCarePlans = async () => {
    try {
      const response = await axios.get(`${API}/care-plans?client_id=${id}`);
      setCarePlans(response.data);
    } catch (error) {
      console.error("Failed to fetch care plans:", error);
    }
  };

  const fetchCarePlanPOCs = async () => {
    try {
      const response = await axios.get(`${API}/care-plan-poc?client_id=${id}`);
      setCarePlanPOCs(response.data);
    } catch (error) {
      console.error("Failed to fetch Care Plan POCs:", error);
    }
  };

  const handleCreateCarePlanPOC = async () => {
    if (!newCarePlanPOC.soc_date) {
      toast.error("Please enter Start of Care date");
      return;
    }

    setSavingCarePlanPOC(true);
    try {
      if (editingPocId) {
        // Update existing POC
        await axios.patch(`${API}/care-plan-poc/${editingPocId}`, {
          ...newCarePlanPOC,
          hours_per_week: newCarePlanPOC.hours_per_week ? parseFloat(newCarePlanPOC.hours_per_week) : null,
        });
        toast.success("Care Plan POC updated!");
      } else {
        // Create new POC
        await axios.post(`${API}/care-plan-poc`, {
          client_id: id,
          ...newCarePlanPOC,
          hours_per_week: newCarePlanPOC.hours_per_week ? parseFloat(newCarePlanPOC.hours_per_week) : null,
        });
        toast.success("Care Plan POC created!");
      }
      setCarePlanPOCDialogOpen(false);
      setEditingPocId(null);
      resetCarePlanPOCForm();
      fetchCarePlanPOCs();
    } catch (error) {
      console.error("Failed to save Care Plan POC:", error);
      toast.error(error.response?.data?.detail || "Failed to save Care Plan POC");
    } finally {
      setSavingCarePlanPOC(false);
    }
  };

  const resetCarePlanPOCForm = () => {
    setNewCarePlanPOC({
      preferred_name: "",
      ability_to_sign: "Yes",
      pets: "",
      emergency_contact_name: client?.emergency_contact_name || "",
      emergency_contact_relationship: client?.emergency_contact_relation || "",
      emergency_contact_phone: client?.emergency_contact_phone || "",
      emergency_contact_alt_phone: "",
      family_support: "",
      chronic_health_problems: client?.primary_diagnosis || "",
      sleep_habits: "",
      precautions: "",
      dme: client?.dme_current || "",
      hearing_vision_speech: "",
      mental_status: "",
      allergies: "",
      code_status: "Full Code",
      hours_per_week: "",
      days_of_week: [],
      time_of_visits: "",
      soc_date: new Date().toISOString().split('T')[0],
      adl_bathing: { assistance_type: "", frequency: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
      adl_dressing: { assistance_type: "", frequency: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
      adl_grooming: { assistance_type: "", frequency: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
      adl_meals: { assistance_type: "", frequency: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
      adl_mobility: { assistance_type: "", frequency: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
      adl_toileting: { assistance_type: "", frequency: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
      adl_transfers: { assistance_type: "", frequency: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
      adl_med_reminder: { assistance_type: "", frequency: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
      adl_cleaning: { assistance_type: "", frequency: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
      adl_laundry: { assistance_type: "", frequency: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
      adl_other: { assistance_type: "", frequency: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
      other_adl_description: "",
      parameters: "",
      interventions: "",
      goals: "",
    });
  };

  const openNewCarePlanPOC = () => {
    // Pre-fill with client data
    setNewCarePlanPOC(prev => ({
      ...prev,
      emergency_contact_name: client?.emergency_contact_name || "",
      emergency_contact_relationship: client?.emergency_contact_relation || "",
      emergency_contact_phone: client?.emergency_contact_phone || "",
      chronic_health_problems: client?.primary_diagnosis || "",
      dme: client?.dme_current || "",
    }));
    setEditingPocId(null); // Clear edit mode
    setCarePlanPOCDialogOpen(true);
  };

  const openEditCarePlanPOC = (poc) => {
    // Check if fully signed - cannot edit
    if (poc.rn_signature && poc.client_signature && poc.pcw_signature) {
      toast.error("Cannot edit - all signatures have been collected");
      return;
    }
    
    // Populate the form with existing data
    setNewCarePlanPOC({
      preferred_name: poc.preferred_name || "",
      ability_to_sign: poc.ability_to_sign || "Yes",
      pets: poc.pets || "",
      emergency_contact_name: poc.emergency_contact_name || "",
      emergency_contact_relationship: poc.emergency_contact_relationship || "",
      emergency_contact_phone: poc.emergency_contact_phone || "",
      emergency_contact_alt_phone: poc.emergency_contact_alt_phone || "",
      family_support: poc.family_support || "",
      pharmacy_name: poc.pharmacy_name || "",
      pharmacy_phone: poc.pharmacy_phone || "",
      primary_care_physician: poc.primary_care_physician || "",
      pcm_phone: poc.pcm_phone || "",
      chronic_health_problems: poc.chronic_health_problems || "",
      dme: poc.dme || "",
      dialysis_info: poc.dialysis_info || "",
      authorized_visitors: poc.authorized_visitors || "",
      activity_tolerance: poc.activity_tolerance || "",
      pain_level: poc.pain_level || "",
      pain_location: poc.pain_location || "",
      adl_bathing: poc.adl_bathing || { type: "", assistance: "", schedule: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
      adl_dressing: poc.adl_dressing || { type: "", assistance: "", schedule: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
      adl_grooming: poc.adl_grooming || { type: "", assistance: "", schedule: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
      adl_meals: poc.adl_meals || { type: "", assistance: "", schedule: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
      adl_mobility: poc.adl_mobility || { type: "", assistance: "", schedule: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
      adl_toileting: poc.adl_toileting || { type: "", assistance: "", schedule: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
      adl_transfers: poc.adl_transfers || { type: "", assistance: "", schedule: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
      adl_med_reminder: poc.adl_med_reminder || { type: "", assistance: "", schedule: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
      adl_cleaning: poc.adl_cleaning || { type: "", assistance: "", schedule: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
      adl_laundry: poc.adl_laundry || { type: "", assistance: "", schedule: "", mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
      hours_per_week: poc.hours_per_week || "",
      soc_date: poc.soc_date || "",
      days_of_week: poc.days_of_week || [],
      time_of_visits: poc.time_of_visits || "",
      additional_instructions: poc.additional_instructions || "",
    });
    setEditingPocId(poc.id);
    setCarePlanPOCDialogOpen(true);
  };

  const handleSignCarePlanPOC = async (signature) => {
    if (!signingPocId || !pocSignerName) {
      toast.error("Please enter signer name");
      return;
    }

    try {
      await axios.post(`${API}/care-plan-poc/${signingPocId}/sign`, {
        signature,
        signer_type: pocSignerType,
        signer_name: pocSignerName,
      });
      toast.success("Signature added!");
      setPocSignatureDialogOpen(false);
      setSigningPocId(null);
      setPocSignerName("");
      fetchCarePlanPOCs();
    } catch (error) {
      console.error("Failed to sign Care Plan POC:", error);
      toast.error("Failed to add signature");
    }
  };

  const handleDownloadCarePlanPOCPdf = async (planId, clientName) => {
    try {
      const response = await axios.get(`${API}/care-plan-poc/${planId}/pdf`, {
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `CarePlan_POC_${clientName.replace(' ', '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF downloaded!");
    } catch (error) {
      console.error("Failed to download PDF:", error);
      toast.error("Failed to download PDF");
    }
  };

  const openPocSignatureDialog = (planId, type) => {
    setSigningPocId(planId);
    setPocSignerType(type);
    setPocSignerName("");
    setPocSignatureDialogOpen(true);
  };

  const updateADL = (adlKey, field, value) => {
    setNewCarePlanPOC(prev => ({
      ...prev,
      [adlKey]: {
        ...prev[adlKey],
        [field]: value
      }
    }));
  };

  const toggleDayOfWeek = (day) => {
    setNewCarePlanPOC(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(day)
        ? prev.days_of_week.filter(d => d !== day)
        : [...prev.days_of_week, day]
    }));
  };

  // RN Review functions
  const openRnReviewDialog = (pocId) => {
    setReviewingPocId(pocId);
    setRnReviewNotes("");
    setRnReviewSignature(null);
    setRnReviewDialogOpen(true);
  };

  const handleAddRnReview = async () => {
    if (!rnReviewNotes.trim()) {
      toast.error("Please enter review notes");
      return;
    }
    
    if (!rnReviewSignature) {
      toast.error("Please sign to complete the review");
      return;
    }

    setSavingRnReview(true);
    try {
      // Use the dedicated RN review endpoint with signature
      await axios.post(`${API}/care-plan-poc/${reviewingPocId}/rn-review`, {
        notes: rnReviewNotes,
        signature: rnReviewSignature,
        signer_name: user?.name || "RN"
      });
      
      toast.success("RN Review added with signature!");
      setRnReviewDialogOpen(false);
      setRnReviewNotes("");
      setRnReviewSignature(null);
      setReviewingPocId(null);
      fetchCarePlanPOCs();
    } catch (error) {
      console.error("Failed to add RN review:", error);
      toast.error(error.response?.data?.detail || "Failed to add review");
    } finally {
      setSavingRnReview(false);
    }
  };

  const openViewReviewsDialog = (poc) => {
    setViewingPoc(poc);
    setViewReviewsDialogOpen(true);
  };

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/documents?client_id=${id}`);
      setDocuments(response.data);
    } catch (error) {
      console.error("Failed to fetch documents:", error);
    }
  };

  const fetchCms485Forms = async () => {
    try {
      const response = await axios.get(`${API}/cms485?client_id=${id}`);
      setCms485Forms(response.data);
    } catch (error) {
      console.error("Failed to fetch CMS 485 forms:", error);
    }
  };

  const fetchFaxOrders = async () => {
    try {
      const response = await axios.get(`${API}/fax/orders?client_id=${id}`);
      setFaxOrders(response.data);
    } catch (error) {
      console.error("Failed to fetch fax orders:", error);
    }
  };

  const fetchSignLinks = async () => {
    try {
      const response = await axios.get(`${API}/physician-sign-link`);
      // Filter by client's documents
      const clientDocs = [...cms485Forms.map(f => f.id), ...carePlanPOCs.map(p => p.id)];
      const clientLinks = response.data.filter(link => clientDocs.includes(link.document_id));
      setSignLinks(clientLinks);
    } catch (error) {
      console.error("Failed to fetch sign links:", error);
    }
  };

  // MD Order Edit handlers
  const openViewMdOrder = (order) => {
    setViewingMdOrder(order);
    setViewMdOrderDialogOpen(true);
  };

  const openEditMdOrder = (order) => {
    if (order.physician_signature) {
      toast.error("Cannot edit - order has been signed");
      return;
    }
    setEditingMdOrder({
      id: order.id,
      order_types: order.order_types || [],
      notes: order.notes || "",
      physician_name: order.physician_name || "",
      physician_phone: order.physician_phone || "",
      physician_fax: order.physician_fax || "",
      physician_npi: order.physician_npi || "",
      patient_height: order.patient_height || client?.height || "",
      patient_weight: order.patient_weight || client?.weight || "",
      md_visit_notes: order.md_visit_notes || "",
      dme_items: order.dme_items || [],
      dme_other: order.dme_other || "",
      hold_reason: order.hold_reason || "",
      hold_reason_other: order.hold_reason_other || "",
      hold_start_date: order.hold_start_date || "",
      hold_expected_return: order.hold_expected_return || "",
      hold_instructions: order.hold_instructions || "",
      discharge_reason: order.discharge_reason || "",
      discharge_reason_other: order.discharge_reason_other || "",
      discharge_date: order.discharge_date || "",
      discharge_last_service_date: order.discharge_last_service_date || "",
      discharge_instructions: order.discharge_instructions || "",
    });
    setEditMdOrderDialogOpen(true);
  };

  const handleSaveMdOrder = async () => {
    if (!editingMdOrder) return;
    
    try {
      await axios.patch(`${API}/md-orders/${editingMdOrder.id}`, editingMdOrder);
      toast.success("MD Order updated!");
      setEditMdOrderDialogOpen(false);
      setEditingMdOrder(null);
      fetchMdOrders();
    } catch (error) {
      console.error("Failed to update MD Order:", error);
      toast.error(error.response?.data?.detail || "Failed to update MD Order");
    }
  };

  const handleSendEfax = async () => {
    if (!efaxData.recipient_fax || !efaxData.recipient_name) {
      toast.error("Please enter recipient fax number and name");
      return;
    }

    if (!efaxData.recipient_fax.startsWith('+')) {
      toast.error("Fax number must be in E.164 format (e.g., +12025551234)");
      return;
    }

    setSendingEfax(true);
    try {
      const res = await axios.post(`${API}/fax/send`, {
        client_id: id,
        ...efaxData
      });
      const status = res.data?.status;
      const errMsg = res.data?.error_message;
      const provider = (res.data?.provider || "").toUpperCase() || "your provider";
      if (status === "sent") {
        toast.success(`Fax sent via ${provider}`);
      } else if (status === "failed") {
        toast.error(`Fax failed: ${errMsg || "Unknown error"}`);
      } else {
        toast.warning(`Fax status: ${status || "queued"}`);
      }
      setEfaxDialogOpen(false);
      setEfaxData({
        recipient_fax: "",
        recipient_name: "",
        document_type: "cms485",
        document_id: "",
        notes: "",
        urgent: false,
      });
      fetchFaxOrders();
    } catch (error) {
      console.error("Failed to send fax:", error);
      toast.error(error.response?.data?.detail || "Failed to send fax");
    } finally {
      setSendingEfax(false);
    }
  };

  const handleCreateSignLink = async () => {
    if (!signLinkData.physician_name || !signLinkData.document_id) {
      const label = (signLinkData.signer_role || "physician") === "nurse" ? "nurse" : "physician";
      toast.error(`Please select a document and enter ${label} name`);
      return;
    }

    setCreatingSignLink(true);
    try {
      const response = await axios.post(`${API}/physician-sign-link`, signLinkData);
      
      const secureUrl = response.data.secure_url;
      const emailStatus = response.data.email_status;
      const toEmail = (signLinkData.physician_email || "").trim();
      setCreatedSignLinkMeta({
        id: response.data.id,
        name: signLinkData.physician_name,
        role: signLinkData.signer_role || "physician",
        email: toEmail,
        email_status: emailStatus,
        email_error: response.data.email_error,
      });
      
      if (secureUrl) {
        // Store the URL to show in dialog
        setCreatedSignLinkUrl(secureUrl);
        
        if (toEmail && emailStatus === "sent") {
          toast.success(`Sign link emailed to ${toEmail}`);
        } else if (toEmail) {
          toast.error(`Link created but email to ${toEmail} failed — copy the link to share it`);
        } else {
          // Try to copy to clipboard
          try {
            await navigator.clipboard.writeText(secureUrl);
            toast.success("Sign link created and copied to clipboard!");
          } catch (clipboardError) {
            console.warn("Clipboard copy failed:", clipboardError);
            toast.success("Sign link created! Copy it from the dialog.");
          }
        }
      } else {
        toast.success("Sign link created!");
        setSignLinkDialogOpen(false);
      }
      
      setSignLinkData({
        document_type: "cms485",
        document_id: "",
        physician_name: "",
        physician_email: "",
        signer_role: "physician",
        expires_days: 7
      });
      fetchSignLinks();
    } catch (error) {
      console.error("Failed to create sign link:", error);
      toast.error(error.response?.data?.detail || "Failed to create sign link");
    } finally {
      setCreatingSignLink(false);
    }
  };
  
  const handleCopySignLink = async () => {
    if (createdSignLinkUrl) {
      try {
        await navigator.clipboard.writeText(createdSignLinkUrl);
        toast.success("Link copied to clipboard!");
      } catch (err) {
        // Fallback - select the text
        const textArea = document.createElement('textarea');
        textArea.value = createdSignLinkUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        toast.success("Link copied!");
      }
    }
  };
  
  const handleResendSignLinkEmail = async (email) => {
    if (!createdSignLinkMeta?.id || !email) return;
    try {
      await axios.post(`${API}/physician-sign-link/${createdSignLinkMeta.id}/resend`, { email });
      setCreatedSignLinkMeta((m) => ({ ...m, email, email_status: "sent", email_error: null }));
      toast.success(`Sign link emailed to ${email}`);
    } catch (error) {
      const detail = error.response?.data?.detail || "Email send failed";
      setCreatedSignLinkMeta((m) => ({ ...m, email, email_status: "error", email_error: detail }));
      toast.error(detail);
    }
  };
  
  const handleCloseSignLinkDialog = () => {
    setSignLinkDialogOpen(false);
    setCreatedSignLinkUrl(null);
    setCreatedSignLinkMeta(null);
  };

  const openFaxDialog = (docType, docId, recipientName = "") => {
    // Pre-fill with physician info if available
    const physicianName = client?.physician?.name || recipientName;
    const physicianFax = client?.physician?.fax || "";
    
    setEfaxData({
      recipient_fax: physicianFax,
      recipient_name: physicianName,
      document_type: docType,
      document_id: docId,
      notes: ""
    });
    setEfaxDialogOpen(true);
  };

  // One-click re-fax for a document — reuses recipient/notes/urgent from
  // the last dispatched fax for the same (document_type, document_id).
  const handleQuickRefax = async (docType, docId) => {
    try {
      const res = await axios.post(
        `${API}/fax/documents/${docType}/${docId}/quick-refax`,
        {}
      );
      if (res.data?.success) {
        toast.success(
          `Re-fax sent via ${(res.data.provider || "").toUpperCase() || "provider"}`
        );
      } else {
        toast.error(res.data?.error || res.data?.message || "Re-fax could not be sent");
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 404) {
        toast.error(detail || "No previous fax found — use Send Fax first.");
      } else {
        toast.error(detail || "Re-fax request failed");
      }
    }
  };


  const openSignLinkDialog = (docType, docId, signerRole = "physician") => {
    // Default the pre-filled name to the client's physician when we're
    // creating a physician sign link; for nurse links start blank so the
    // admin fills in whichever RN they want to send it to.
    const defaultName = signerRole === "nurse"
      ? ""
      : (client?.physician?.name || "");

    setSignLinkData({
      document_type: docType,
      document_id: docId,
      physician_name: defaultName,
      physician_email: "",
      signer_role: signerRole,
      expires_days: 7
    });
    setSignLinkDialogOpen(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Link copied to clipboard!");
  };

  const fetchVisitNotes = async () => {
    try {
      const response = await axios.get(`${API}/visit-notes?client_id=${id}`);
      setVisitNotes(response.data);
    } catch (error) {
      console.error("Failed to fetch visit notes:", error);
    }
  };

  const handleCreateVisitNote = async () => {
    if (!newVisitNote.visit_date) {
      toast.error("Please enter visit date");
      return;
    }

    setSavingVisitNote(true);
    try {
      await axios.post(`${API}/visit-notes`, {
        client_id: id,
        ...newVisitNote,
      });
      toast.success("Caregiver visit note created!");
      setVisitNoteDialogOpen(false);
      setNewVisitNote({
        visit_date: new Date().toISOString().split('T')[0],
        time_in: "",
        time_out: "",
        visit_type: "routine",
        duration_minutes: 60,
        services_provided: [],
        vital_signs: { blood_pressure: "", heart_rate: "", temperature: "", oxygen_saturation: "" },
        pain_level: null,
        mots_observations: "",
        patient_condition: "stable",
        patient_response: "",
        follow_up_needed: false,
        follow_up_notes: "",
        caregiver_signature: null,
        client_signature: null,
      });
      fetchVisitNotes();
    } catch (error) {
      console.error("Failed to create visit note:", error);
      toast.error("Failed to create visit note");
    } finally {
      setSavingVisitNote(false);
    }
  };

  const handleSignCms485 = async (signature) => {
    if (!signingFormId || !signerName) {
      toast.error("Please enter signer name");
      return;
    }

    try {
      await axios.post(`${API}/cms485/${signingFormId}/sign`, {
        signature,
        signer_type: signerType,
        signer_name: signerName,
      });
      toast.success("Signature added!");
      setSignatureDialogOpen(false);
      setSigningFormId(null);
      setSignerName("");
      fetchCms485Forms();
    } catch (error) {
      console.error("Failed to sign form:", error);
      toast.error("Failed to add signature");
    }
  };

  const handleDownloadCms485Pdf = async (formId) => {
    try {
      const response = await axios.get(`${API}/cms485/${formId}/pdf`, {
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `CMS485_${client.first_name}_${client.last_name}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF downloaded!");
    } catch (error) {
      console.error("Failed to download PDF:", error);
      toast.error("Failed to download PDF");
    }
  };

  const handleDownloadMdOrderPdf = async (orderId) => {
    try {
      const response = await axios.get(`${API}/md-orders/${orderId}/pdf`, {
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `MD_Order_${client.first_name}_${client.last_name}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("MD Order PDF downloaded!");
    } catch (error) {
      console.error("Failed to download MD Order PDF:", error);
      toast.error("Failed to download PDF");
    }
  };

  const openSignatureDialog = (formId, type) => {
    setSigningFormId(formId);
    setSignerType(type);
    
    // Pre-fill with RN name for agency signature
    if (type === "agency") {
      const form = cms485Forms.find(f => f.id === formId);
      setSignerName(form?.agency_rn_name || "");
    } else {
      setSignerName("");
    }
    
    setSignatureDialogOpen(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File size must be less than 10MB");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUploadDocument = async () => {
    if (!selectedFile) {
      toast.error("Please select a file");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("client_id", id);
      formData.append("document_type", documentType);
      formData.append("notes", documentNotes);

      await axios.post(`${API}/documents/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Document uploaded successfully!");
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setDocumentType("other");
      setDocumentNotes("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetchDocuments();
    } catch (error) {
      console.error("Failed to upload document:", error);
      toast.error("Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      await axios.delete(`${API}/documents/${docId}`);
      toast.success("Document deleted");
      fetchDocuments();
    } catch (error) {
      console.error("Failed to delete document:", error);
      toast.error("Failed to delete document");
    }
  };

  const handleCreateCms485 = async () => {
    if (!cms485Form.certification_period_from || !cms485Form.certification_period_to) {
      toast.error("Please fill in certification period dates");
      return;
    }

    setSavingCms485(true);
    try {
      if (editingCms485Id) {
        // Update existing form - include signature clearing if needed
        const updateData = { ...cms485Form };
        if (clearCms485Signatures) {
          updateData.agency_signature = null;
          updateData.physician_signature = null;
          updateData.signed_by_agency = null;
          updateData.signed_by_physician = null;
          updateData.agency_signature_date = null;
          updateData.physician_signature_date = null;
          updateData.status = "draft";
        }
        await axios.patch(`${API}/cms485/${editingCms485Id}`, updateData);
        toast.success(clearCms485Signatures 
          ? "CMS 485 form updated! Signatures have been cleared." 
          : "CMS 485 form updated!");
      } else {
        // Create new form
        await axios.post(`${API}/cms485`, {
          client_id: id,
          ...cms485Form,
        });
        toast.success("CMS 485 form created!");
      }
      setCms485DialogOpen(false);
      setEditingCms485Id(null);
      setClearCms485Signatures(false);
      setCms485Form({
        certification_period_from: "",
        certification_period_to: "",
        principal_diagnosis: client?.primary_diagnosis || "",
        surgical_procedures: "",
        other_diagnoses: client?.secondary_diagnosis || "",
        dme_supplies: client?.dme_current || "",
        safety_measures: "",
        nutritional_requirements: "",
        allergies: "",
        functional_limitations: [],
        activities_permitted: [],
        mental_status: [],
        prognosis: "good",
        orders_for_discipline: "",
        goals: "",
        discharge_plans: "",
        physician_id: "",
        physician_name: client?.physician?.name || "",
        physician_phone: client?.physician?.phone || "",
        physician_address: "",
        agency_rn_id: "",
        agency_rn_name: "",
        // PCW Service Fields
        pcw_hours_per_day: "",
        pcw_days_per_week: "",
        pcw_weeks: "",
        pcw_poc_days_per_week: "",
        pcw_prn_hours: false,
        pcw_tasks: {
          bathing: { assistance: "", frequency: "" },
          dressing_upper_body: { assistance: "", frequency: "" },
          dressing_lower_body: { assistance: "", frequency: "" },
          grooming: { assistance: "", frequency: "" },
          eating: { assistance: "", frequency: "" },
          mobility_in_home: { assistance: "", frequency: "" },
          toileting: { assistance: "", frequency: "" },
          transfers: { assistance: "", frequency: "" },
        },
        pcw_mots: "",
        pcw_interventions_parameters: "",
      });
      fetchCms485Forms();
    } catch (error) {
      console.error("Failed to save CMS 485:", error);
      toast.error(editingCms485Id ? "Failed to update CMS 485 form" : "Failed to create CMS 485 form");
    } finally {
      setSavingCms485(false);
    }
  };

  const openNewCms485 = () => {
    setEditingCms485Id(null);
    // Pre-fill with everything we already know about the client so the form
    // doesn't have to be re-typed. User-editable — anything the client record
    // doesn't have stays blank.
    const secondaryDx = Array.isArray(client?.secondary_diagnoses)
      ? client.secondary_diagnoses.filter(Boolean).join(", ")
      : (client?.secondary_diagnosis || "");
    setCms485Form({
      certification_period_from: "",
      certification_period_to: "",
      principal_diagnosis: client?.primary_diagnosis || "",
      secondary_diagnosis: secondaryDx,
      surgical_procedures: "",
      other_diagnoses: secondaryDx,
      dme_supplies: client?.dme_current || "",
      safety_measures: client?.has_seizure_history ? "Seizure precautions" : "",
      nutritional_requirements: client?.is_diabetic ? "Diabetic diet" : "",
      pcw_mots: client?.is_diabetic ? DIABETIC_MOT : "",
      allergies: client?.allergies || "",
      functional_limitations: [],
      activities_permitted: [],
      mental_status: [],
      prognosis: "good",
      orders_for_discipline: "",
      orders_discipline_list: [],
      goals: "",
      discharge_plans: "",
      physician_id: "",
      physician_name: client?.physician?.name || "",
      physician_phone: client?.physician?.phone || "",
      physician_address:
        client?.physician?.address
          ? `${client.physician.address}${client?.physician?.clinic_name ? ` — ${client.physician.clinic_name}` : ""}`
          : (client?.physician?.clinic_name || ""),
      agency_rn_id: "",
      agency_rn_name: "",
      // PCW Service Fields
      pcw_hours_per_day: "",
      pcw_days_per_week: "",
      pcw_weeks: "",
      pcw_poc_days_per_week: "",
      pcw_prn_hours: false,
      pcw_tasks: {
        bathing: { assistance: "", frequency: "" },
        dressing_upper_body: { assistance: "", frequency: "" },
        dressing_lower_body: { assistance: "", frequency: "" },
        grooming: { assistance: "", frequency: "" },
        eating: { assistance: "", frequency: "" },
        mobility_in_home: { assistance: "", frequency: "" },
        toileting: { assistance: "", frequency: "" },
        transfers: { assistance: "", frequency: "" },
      },
      pcw_interventions_parameters: "",
    });
    setCms485DialogOpen(true);
    axios.get(`${API}/assessments/cms485-prefill`, { params: { client_id: id } }).then((r) => {
      if (!r.data?.found) return;
      const p = r.data.prefill || {};
      setCms485Form((prev) => ({
        ...prev,
        principal_diagnosis: p.principal_diagnosis || prev.principal_diagnosis,
        other_diagnoses: p.other_diagnoses || prev.other_diagnoses,
        allergies: p.allergies || prev.allergies,
        dme_supplies: p.dme_supplies || prev.dme_supplies,
        safety_measures: p.safety_measures || prev.safety_measures,
        nutritional_requirements: p.nutritional_requirements || prev.nutritional_requirements,
        mental_status: p.mental_status?.length ? p.mental_status : prev.mental_status,
        functional_limitations: p.functional_limitations?.length ? p.functional_limitations : prev.functional_limitations,
        prognosis: p.prognosis || prev.prognosis,
      }));
      toast.success(`Pre-filled from RN ${r.data.type_label} assessment (${r.data.visit_date})`);
    }).catch(() => {});
  };

  const normalizePcwTasks = (tasks) => {
    const t = { ...(tasks || {}) };
    const filled = (v) => v && (v.assistance || v.frequency);
    if (filled(t.dressing)) {
      if (!filled(t.dressing_upper_body)) t.dressing_upper_body = { ...t.dressing };
      if (!filled(t.dressing_lower_body)) t.dressing_lower_body = { ...t.dressing };
      delete t.dressing;
    }
    if (filled(t.mobility) && !filled(t.mobility_in_home)) {
      t.mobility_in_home = { ...t.mobility };
      delete t.mobility;
    }
    return t;
  };

  const openEditCms485 = (form, clearSignatures = false) => {
    setEditingCms485Id(form.id);
    setClearCms485Signatures(clearSignatures);
    setCms485Form({
      certification_period_from: form.certification_period_from || "",
      certification_period_to: form.certification_period_to || "",
      principal_diagnosis: form.principal_diagnosis || "",
      secondary_diagnosis: form.secondary_diagnosis || "",
      surgical_procedures: form.surgical_procedures || "",
      other_diagnoses: form.other_diagnoses || "",
      dme_supplies: form.dme_supplies || "",
      nutritional_requirements: (() => {
        const nr = form.nutritional_requirements || "";
        if (client?.is_diabetic && !/diabet/i.test(nr)) return nr ? `${nr}; Diabetic diet` : "Diabetic diet";
        return nr;
      })(),
      pcw_mots: (() => {
        const m = form.pcw_mots || "";
        if (client?.is_diabetic && !/glucose|blood sugar/i.test(m)) return m ? `${m}; ${DIABETIC_MOT}` : DIABETIC_MOT;
        return m;
      })(),
      safety_measures: (() => {
        const sm = form.safety_measures || "";
        if (client?.has_seizure_history && !/seizure/i.test(sm)) return sm ? `${sm}; Seizure precautions` : "Seizure precautions";
        return sm;
      })(),
      allergies: form.allergies || "",
      functional_limitations: form.functional_limitations || [],
      activities_permitted: form.activities_permitted || [],
      mental_status: form.mental_status || [],
      prognosis: form.prognosis || "good",
      orders_for_discipline: form.orders_for_discipline || "",
      orders_discipline_list: Array.isArray(form.orders_discipline_list) ? form.orders_discipline_list : [],
      goals: form.goals || "",
      discharge_plans: form.discharge_plans || "",
      physician_id: form.physician_id || "",
      physician_name: form.physician_name || "",
      physician_phone: form.physician_phone || "",
      physician_address: form.physician_address || "",
      agency_rn_id: form.agency_rn_id || "",
      agency_rn_name: form.agency_rn_name || "",
      // PCW Service Fields
      pcw_hours_per_day: form.pcw_hours_per_day || "",
      pcw_days_per_week: form.pcw_days_per_week || "",
      pcw_weeks: form.pcw_weeks || "",
      pcw_poc_days_per_week: form.pcw_poc_days_per_week || "",
      pcw_prn_hours: form.pcw_prn_hours || false,
      pcw_tasks: form.pcw_tasks ? normalizePcwTasks(form.pcw_tasks) : {
        bathing: { assistance: "", frequency: "" },
        dressing_upper_body: { assistance: "", frequency: "" },
        dressing_lower_body: { assistance: "", frequency: "" },
        grooming: { assistance: "", frequency: "" },
        eating: { assistance: "", frequency: "" },
        mobility_in_home: { assistance: "", frequency: "" },
        toileting: { assistance: "", frequency: "" },
        transfers: { assistance: "", frequency: "" },
      },
      pcw_interventions_parameters: form.pcw_interventions_parameters || "",
    });
    setCms485DialogOpen(true);
  };

  const handleSendFaxOrder = async () => {
    const selectedTypes = Object.entries(faxOrderTypes)
      .filter(([_, selected]) => selected)
      .map(([type]) => type);
    
    if (selectedTypes.length === 0) {
      toast.error("Please select at least one order type");
      return;
    }

    if (faxOrderTypes.discharge && (!faxDischarge.discharge_reason || !faxDischarge.discharge_date)) {
      toast.error("Please select a discharge reason and effective discharge date");
      return;
    }
    if (faxOrderTypes.service_hold && !faxHold.hold_start_date) {
      toast.error("Please enter the hold start date");
      return;
    }

    setSendingFax(true);
    try {
      await axios.post(`${API}/md-orders`, {
        client_id: id,
        order_types: selectedTypes,
        notes: faxNotes,
        physician_name: client.physician?.name,
        physician_fax: client.physician?.fax,
        physician_phone: client.physician?.phone,
        physician_npi: client.physician?.npi,
        ...(faxOrderTypes.discharge ? faxDischarge : {}),
        ...(faxOrderTypes.service_hold ? faxHold : {}),
        ...(faxOrderTypes.dme_supplies ? {
          dme_other: faxDme.dme_other || null,
          patient_height: faxDme.patient_height || client?.height || null,
          patient_weight: faxDme.patient_weight || client?.weight || null,
        } : {}),
      });
      toast.success("MD Order request submitted!");
      setFaxDialogOpen(false);
      setFaxOrderTypes({
        personal_care: false,
        dme_supplies: false,
        service_hold: false,
        discharge: false,
      });
      setFaxNotes("");
      setFaxDischarge(EMPTY_DISCHARGE);
      setFaxHold(EMPTY_HOLD);
      setFaxDme({ dme_other: "", patient_height: "", patient_weight: "" });
      fetchMdOrders();
    } catch (error) {
      console.error("Failed to submit MD order:", error);
      toast.error("Failed to submit order request");
    } finally {
      setSendingFax(false);
    }
  };

  const handleCreateCarePlan = async () => {
    if (!newCarePlan.title || !newCarePlan.goals) {
      toast.error("Please fill in title and goals");
      return;
    }

    setSavingCarePlan(true);
    try {
      await axios.post(`${API}/care-plans`, {
        client_id: id,
        ...newCarePlan,
      });
      toast.success("Care plan created!");
      setCarePlanDialogOpen(false);
      setNewCarePlan({ title: "", goals: "", interventions: "", frequency: "" });
      fetchCarePlans();
    } catch (error) {
      console.error("Failed to create care plan:", error);
      toast.error("Failed to create care plan");
    } finally {
      setSavingCarePlan(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      await axios.patch(`${API}/clients/${id}/status?status=${newStatus}`);
      setClient((prev) => ({ ...prev, status: newStatus }));
      toast.success("Status updated successfully");
    } catch (error) {
      console.error("Failed to update status:", error);
      toast.error("Failed to update status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/clients/${id}`);
      toast.success("Client deleted successfully");
      navigate("/clients");
    } catch (error) {
      console.error("Failed to delete client:", error);
      toast.error("Failed to delete client");
    }
  };

  const handlePrintFaceSheet = () => {
    const printWindow = window.open('', '_blank');
    const today = new Date().toLocaleDateString();
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Face Sheet - ${client.first_name} ${client.last_name}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            font-size: 11px; 
            padding: 20px;
            max-width: 8.5in;
            margin: 0 auto;
          }
          .header { 
            text-align: center; 
            border-bottom: 2px solid #333; 
            padding-bottom: 8px; 
            margin-bottom: 12px;
          }
          .header h1 { font-size: 18px; margin-bottom: 2px; }
          .header p { font-size: 10px; color: #666; }
          .client-name { 
            font-size: 16px; 
            font-weight: bold; 
            text-align: center;
            background: #f0f0f0;
            padding: 6px;
            margin-bottom: 12px;
          }
          .two-col { display: flex; gap: 20px; }
          .col { flex: 1; }
          .section { margin-bottom: 10px; }
          .section-title { 
            font-size: 11px; 
            font-weight: bold; 
            background: #e0e0e0; 
            padding: 3px 6px; 
            margin-bottom: 4px;
            text-transform: uppercase;
          }
          .row { 
            display: flex; 
            border-bottom: 1px solid #ddd; 
            padding: 2px 0;
          }
          .label { 
            font-weight: bold; 
            width: 120px; 
            flex-shrink: 0;
            font-size: 10px;
          }
          .value { flex: 1; font-size: 10px; }
          .conditions { display: flex; gap: 20px; margin-top: 4px; }
          .condition-item { font-size: 10px; }
          .condition-yes { color: #d00; font-weight: bold; }
          .condition-no { color: #060; }
          .footer { 
            margin-top: 15px; 
            padding-top: 8px; 
            border-top: 1px solid #333;
            font-size: 9px;
            color: #666;
            text-align: center;
          }
          @media print {
            body { padding: 10px; }
            @page { margin: 0.5in; size: letter; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>CLIENT FACE SHEET</h1>
          <p>Confidential Medical Information</p>
        </div>
        
        <div class="client-name">
          ${client.first_name} ${client.last_name}
          ${client.dob ? ` | DOB: ${client.dob}` : ''}
          ${client.ssn_last4 ? ` | SSN: ***-**-${client.ssn_last4}` : ''}
        </div>

        <div class="two-col">
          <div class="col">
            <div class="section">
              <div class="section-title">Contact Information</div>
              <div class="row"><span class="label">Phone:</span><span class="value">${client.phone || 'N/A'}</span></div>
              <div class="row"><span class="label">Address:</span><span class="value">${client.address || 'N/A'}</span></div>
              <div class="row"><span class="label">City/State/ZIP:</span><span class="value">${client.city || ''}, ${client.state || ''} ${client.zip_code || ''}</span></div>
            </div>

            <div class="section">
              <div class="section-title">Emergency Contact</div>
              <div class="row"><span class="label">Name:</span><span class="value">${client.emergency_contact_name || 'N/A'}</span></div>
              <div class="row"><span class="label">Phone:</span><span class="value">${client.emergency_contact_phone || 'N/A'}</span></div>
              <div class="row"><span class="label">Relationship:</span><span class="value">${client.emergency_contact_relation || 'N/A'}</span></div>
            </div>

            <div class="section">
              <div class="section-title">Insurance</div>
              <div class="row"><span class="label">Insurance:</span><span class="value">${client.insurance_info || 'N/A'}</span></div>
              <div class="row"><span class="label">Accepted:</span><span class="value">${client.insurance_accepted ? 'Yes' : 'No'}</span></div>
            </div>

            <div class="section">
              <div class="section-title">Physical</div>
              <div class="row"><span class="label">Height:</span><span class="value">${client.height || 'N/A'}</span></div>
              <div class="row"><span class="label">Weight:</span><span class="value">${client.weight || 'N/A'}</span></div>
            </div>
          </div>

          <div class="col">
            <div class="section">
              <div class="section-title">Physician Information</div>
              <div class="row"><span class="label">Physician:</span><span class="value">${client.physician?.name || 'N/A'}</span></div>
              <div class="row"><span class="label">Clinic:</span><span class="value">${client.physician?.clinic_name || 'N/A'}</span></div>
              <div class="row"><span class="label">Phone:</span><span class="value">${client.physician?.phone || 'N/A'}</span></div>
              <div class="row"><span class="label">Fax:</span><span class="value">${client.physician?.fax || 'N/A'}</span></div>
              <div class="row"><span class="label">Last MD Visit:</span><span class="value">${client.last_md_visit || 'N/A'}</span></div>
            </div>

            <div class="section">
              <div class="section-title">Diagnoses</div>
              <div class="row"><span class="label">Primary Dx:</span><span class="value">${client.primary_diagnosis || 'N/A'}</span></div>
              <div class="row"><span class="label">Secondary Dx:</span><span class="value">${client.secondary_diagnoses?.length > 0 ? client.secondary_diagnoses.join(', ') : 'None'}</span></div>
              <div class="row"><span class="label">Care Reasons:</span><span class="value">${client.care_reasons || 'N/A'}</span></div>
            </div>

            <div class="section">
              <div class="section-title">Medical Conditions</div>
              <div class="conditions">
                <span class="condition-item">Diabetic: <span class="${client.is_diabetic ? 'condition-yes' : 'condition-no'}">${client.is_diabetic ? 'YES' : 'No'}</span></span>
                <span class="condition-item">Seizure History: <span class="${client.has_seizure_history ? 'condition-yes' : 'condition-no'}">${client.has_seizure_history ? 'YES' : 'No'}</span></span>
              </div>
              <div class="row" style="margin-top:6px"><span class="label">Allergies:</span><span class="value" style="${client.allergies && !/^(none|nka|nkda|n\/a)$/i.test(client.allergies.trim()) ? 'color:#b91c1c;font-weight:bold;' : ''}">${client.allergies || 'NKDA / none documented'}</span></div>
            </div>

            <div class="section">
              <div class="section-title">DME / Supplies</div>
              <div class="row"><span class="label">Current DME:</span><span class="value">${client.dme_current || 'None'}</span></div>
              <div class="row"><span class="label">DME Needed:</span><span class="value">${client.dme_needed || 'None'}</span></div>
            </div>
          </div>
        </div>

        <div class="footer">
          <p>Generated: ${today} | Status: ${client.status?.replace(/_/g, ' ').toUpperCase() || 'N/A'} | Client ID: ${client.id}</p>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      potential: "badge-potential",
      active: "badge-active",
      discharged: "badge-discharged",
      on_hold: "badge-on_hold",
      inactive: "badge-inactive",
      non_admit: "badge-non_admit",
    };
    return classes[status] || "badge-potential";
  };

  const formatStatus = (status) => {
    return status?.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) || "";
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner w-8 h-8"></div>
        </div>
      </Layout>
    );
  }

  if (!client) {
    return (
      <Layout>
        <div className="text-center py-16">
          <p className="text-slate-500">Client not found</p>
          <Link to="/clients" className="text-primary hover:underline mt-2 inline-block">
            Back to Clients
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <Link to="/clients" className="hover:text-slate-700">
          Clients
        </Link>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">
          {client.first_name} {client.last_name}
        </span>
      </div>

      {/* Back Button */}
      <Link
        to="/clients"
        className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-6"
      >
        <ChevronLeft className="w-4 h-4" />
        Back to Clients
      </Link>

      {/* Profile Header */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xl font-bold flex-shrink-0">
              {client.first_name[0]}
              {client.last_name[0]}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                {client.first_name} {client.last_name}
              </h1>
              <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-slate-500">
                {client.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    {client.phone}
                  </span>
                )}
                {client.dob && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    DOB: {client.dob}
                  </span>
                )}
                {(client.city || client.state) && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {[client.city, client.state].filter(Boolean).join(", ")}
                  </span>
                )}
                {(() => {
                  const count = (documents || []).filter(
                    (d) => d.category === "signed_roi"
                  ).length;
                  return (
                    <button
                      type="button"
                      onClick={() => {
                        setDocsCategoryFilter("signed_roi");
                        setActiveTab("documents");
                      }}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors ${
                        count > 0
                          ? "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                          : "bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100"
                      }`}
                      data-testid="signed-roi-badge"
                      title="View signed Release of Information documents"
                    >
                      <CheckCircle className="w-3.5 h-3.5" />
                      {count} Signed ROI{count === 1 ? "" : "s"}
                    </button>
                  );
                })()}
                {client.admitted_from_referral && (
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200" title={`Referral source: ${client.admitted_from_referral.referral_source || "n/a"} · referred ${client.admitted_from_referral.referral_date || "n/a"}`} data-testid="admitted-from-referral-badge">
                    Admitted from referral · {client.admission_date || (client.admitted_from_referral.admitted_at || "").slice(0, 10)}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Status Select */}
            <Select
              value={client.status}
              onValueChange={handleStatusChange}
              disabled={updatingStatus}
            >
              <SelectTrigger className="w-40" data-testid="status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Link to={`/clients/${id}/edit`}>
              <Button variant="outline" className="gap-2" data-testid="edit-client-btn">
                <Edit className="w-4 h-4" />
                Edit
              </Button>
            </Link>

            <Button
              variant="outline"
              className="gap-2"
              onClick={() => setRoiDialogOpen(true)}
              data-testid="client-send-roi-btn"
            >
              <FileText className="w-4 h-4" />
              Send ROI
            </Button>

            <Button
              variant="default"
              className="gap-2 bg-teal-600 hover:bg-teal-700"
              onClick={() => {
                // Open the Send Fax dialog pre-filled with the client's
                // physician recipient info, but leave the attachment blank
                // so the user can attach any form they want (MD order,
                // CMS-485, custom PDF, etc.).
                const params = new URLSearchParams({
                  action: "send_fax",
                  client_id: id,
                  subject: `MD Order — ${client.first_name || ""} ${client.last_name || ""}`.trim(),
                });
                if (client?.physician?.name) {
                  params.set("recipient_name", client.physician.name);
                }
                if (client?.physician?.fax) {
                  params.set("recipient_fax", client.physician.fax);
                }
                if (client?.physician?.clinic_name) {
                  params.set("recipient_company", client.physician.clinic_name);
                }
                navigate(`/faxes?${params.toString()}`);
              }}
              data-testid="client-send-md-order-btn"
              title="Open the Send Fax dialog pre-filled with the physician's info — attach any document you want"
            >
              <Send className="w-4 h-4" />
              Send MD Order
            </Button>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid="delete-client-btn"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Client</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete {client.first_name} {client.last_name}?
                    This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    className="bg-red-600 hover:bg-red-700"
                    data-testid="confirm-delete-btn"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {/* Clinical Alerts */}
        {(client.is_diabetic || client.has_seizure_history || client.allergies) && (
          <div className="mt-6 space-y-3">
            {client.is_diabetic && (
              <div className="alert-box warning flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Diabetic Client Alert</p>
                  <p className="text-sm mt-1">
                    If client&apos;s blood sugar is less than 60 or greater than 200, PCW is to
                    call RN immediately to report.
                  </p>
                </div>
              </div>
            )}
            {client.has_seizure_history && (
              <div className="alert-box warning flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Seizure Protocol</p>
                  <p className="text-sm mt-1">
                    Keep client in a safe place. Do not place anything in the client&apos;s mouth.
                    PCW to call 911 if seizure lasts longer than 5 minutes.
                  </p>
                </div>
              </div>
            )}
            {client.allergies && (
              <div className="alert-box warning flex items-start gap-3" data-testid="client-allergies-alert">
                <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Allergies</p>
                  <p className="text-sm mt-1 whitespace-pre-wrap">{client.allergies}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-white border border-slate-200 flex-wrap">
          <TabsTrigger value="overview" data-testid="tab-overview">
            <User className="w-4 h-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="medical" data-testid="tab-medical">
            <Stethoscope className="w-4 h-4 mr-2" />
            Medical
          </TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">
            <FileText className="w-4 h-4 mr-2" />
            Notes
          </TabsTrigger>
          <TabsTrigger value="care-plans" data-testid="tab-care-plans">
            <ClipboardList className="w-4 h-4 mr-2" />
            Care Plans
          </TabsTrigger>
          <TabsTrigger value="visit-notes" data-testid="tab-visit-notes">
            <Heart className="w-4 h-4 mr-2" />
            Visit Notes
          </TabsTrigger>
          <TabsTrigger value="assessments" data-testid="tab-assessments">
            <Eye className="w-4 h-4 mr-2" />
            Assessments
          </TabsTrigger>
          <TabsTrigger value="supervisory" data-testid="tab-supervisory">
            <ClipboardList className="w-4 h-4 mr-2" />
            Supervisory
          </TabsTrigger>
          <TabsTrigger value="admission-forms" data-testid="tab-admission-forms">
            <FileText className="w-4 h-4 mr-2" />
            Admission Forms
          </TabsTrigger>
          <TabsTrigger value="schedule" data-testid="tab-schedule">
            <Clock className="w-4 h-4 mr-2" />
            Schedule
          </TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">
            <FolderOpen className="w-4 h-4 mr-2" />
            Documents
          </TabsTrigger>
          <TabsTrigger value="cms485" data-testid="tab-cms485">
            <FilePlus className="w-4 h-4 mr-2" />
            CMS 485
          </TabsTrigger>
          <TabsTrigger value="authorizations" data-testid="tab-authorizations">
            <ClipboardList className="w-4 h-4 mr-2" />
            Authorizations
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <OverviewTab 
            client={client} 
            onPrintFaceSheet={handlePrintFaceSheet} 
            onClientUpdate={(updated) => setClient(updated)}
          />
          <div className="mt-4">
            <ClientSignaturePinCard
              clientId={client.id}
              isAuthorized={["admin", "rn", "case_manager", "scheduler"].includes((user?.role || "").toLowerCase())}
            />
          </div>
        </TabsContent>

        {/* Medical Tab */}
        <TabsContent value="medical">
          <MedicalTab
            client={client}
            mdOrders={mdOrders}
            onSendFaxOrder={() => setFaxDialogOpen(true)}
            onCreateDmeOrder={openDmeOrderFromNeeded}
            onViewMdOrder={openViewMdOrder}
            onEditMdOrder={openEditMdOrder}
            onSendSignLink={openSignLinkDialog}
            onFaxOrder={openFaxDialog}
            onQuickRefax={handleQuickRefax}
            onDownloadPdf={handleDownloadMdOrderPdf}
            onRefresh={fetchClient}
          />
        </TabsContent>

        {/* Notes Tab - RN Notes & Office Notes */}
        <TabsContent value="notes">
          <NotesTab
            rnNotes={rnNotes}
            officeNotes={officeNotes}
            onAddRnNote={() => { setNoteType("rn"); setNewNote(""); setNotesDialogOpen(true); }}
            onAddOfficeNote={() => { setNoteType("office"); setNewNote(""); setNewNoteDate(new Date().toISOString().split('T')[0]); setNotesDialogOpen(true); }}
            onDeleteNote={handleDeleteNote}
            onEditNote={handleEditNote}
            onPrintNote={handlePrintNote}
            onDownloadNote={handleDownloadNote}
            onDownloadAllNotes={handleDownloadAllNotes}
          />
        </TabsContent>

        {/* Care Plans Tab */}
        <TabsContent value="care-plans">
          <CarePlansTab
            carePlanPOCs={carePlanPOCs}
            carePlans={carePlans}
            onNewPOC={openNewCarePlanPOC}
            onEditPOC={openEditCarePlanPOC}
            onViewReviews={openViewReviewsDialog}
            onAddReview={openRnReviewDialog}
            onSignPOC={openPocSignatureDialog}
            onSendSignLink={openSignLinkDialog}
            onFaxPOC={openFaxDialog}
            onDownloadPdf={handleDownloadCarePlanPOCPdf}
            onNewQuickPlan={() => setCarePlanDialogOpen(true)}
          />
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <DocumentsTab
            documents={documents}
            onUploadClick={() => setUploadDialogOpen(true)}
            onDelete={handleDeleteDocument}
            categoryFilter={docsCategoryFilter}
            onCategoryFilterChange={setDocsCategoryFilter}
          />
        </TabsContent>

        {/* CMS 485 Tab */}
        <TabsContent value="cms485">
          <CMS485Tab
            cms485Forms={cms485Forms}
            onNewForm={openNewCms485}
            onEditForm={openEditCms485}
            onSignForm={openSignatureDialog}
            onSendSignLink={openSignLinkDialog}
            onFaxForm={openFaxDialog}
            onQuickRefax={handleQuickRefax}
            onDownloadPdf={handleDownloadCms485Pdf}
          />
        </TabsContent>

        {/* Supervisory Visits Tab */}
        <TabsContent value="assessments">
          <AssessmentsTab
            clientId={id}
            clientName={client ? `${client.first_name} ${client.last_name}` : ""}
            onCompleted={() => { fetchDocuments?.(); }}
          />
        </TabsContent>

        <TabsContent value="supervisory">
          <SupervisoryVisitsTab
            clientId={id}
            clientName={client ? `${client.first_name} ${client.last_name}` : ""}
            supervisoryVisits={supervisoryVisits}
            onDeleteVisit={handleDeleteSupervisoryVisit}
            onCompleted={() => { fetchDocuments?.(); }}
          />
        </TabsContent>

        <TabsContent value="admission-forms">
          <AdmissionFormsTab
            clientId={id}
            clientName={client ? `${client.first_name} ${client.last_name}` : ""}
            onCompleted={() => { fetchDocuments?.(); }}
          />
        </TabsContent>

        {/* Client Schedule Tab */}
        <TabsContent value="schedule">
          <ScheduleTab
            schedule={clientSchedule}
            loading={loadingSchedule}
            onRefresh={fetchClientSchedule}
          />
        </TabsContent>

        {/* Visit Notes Tab */}
        <TabsContent value="visit-notes">
          <VisitNotesTab
            visitNotes={visitNotes}
            onAddNote={() => setVisitNoteDialogOpen(true)}
            clientId={id}
            currentUser={user}
          />
        </TabsContent>

        {/* Authorizations Tab */}
        <TabsContent value="authorizations">
          <ServiceOrdersTab
            serviceOrders={serviceOrders}
            onAddClick={() => {
              resetServiceOrderForm();
              setServiceOrderDialogOpen(true);
            }}
            onEditClick={handleEditServiceOrder}
            onDelete={handleDeleteServiceOrder}
            onClearOrphanedSchedules={handleClearOrphanedSchedules}
          />
        </TabsContent>
      </Tabs>

      {/* Fax MD Order Dialog */}
      {/* Fax MD Order Dialog - Using extracted component */}
      <FaxMDOrderDialog
        open={faxDialogOpen}
        onOpenChange={setFaxDialogOpen}
        faxOrderTypes={faxOrderTypes}
        setFaxOrderTypes={setFaxOrderTypes}
        faxNotes={faxNotes}
        setFaxNotes={setFaxNotes}
        physician={client?.physician}
        sendingFax={sendingFax}
        onSendFax={handleSendFaxOrder}
        discharge={faxDischarge}
        setDischarge={setFaxDischarge}
        hold={faxHold}
        setHold={setFaxHold}
        dme={faxDme}
        setDme={setFaxDme}
        client={client}
      />

      {/* Care Plan Dialog - Using extracted component */}
      <CarePlanDialog
        open={carePlanDialogOpen}
        onOpenChange={setCarePlanDialogOpen}
        clientName={`${client?.first_name} ${client?.last_name}`}
        newCarePlan={newCarePlan}
        setNewCarePlan={setNewCarePlan}
        saving={savingCarePlan}
        onSave={handleCreateCarePlan}
      />

      {/* Document Upload Dialog - Using extracted component */}
      <DocumentUploadDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        clientName={`${client?.first_name} ${client?.last_name}`}
        selectedFile={selectedFile}
        handleFileSelect={handleFileSelect}
        documentType={documentType}
        setDocumentType={setDocumentType}
        documentNotes={documentNotes}
        setDocumentNotes={setDocumentNotes}
        documentTypes={documentTypes}
        uploading={uploading}
        onUpload={handleUploadDocument}
      />

      {/* CMS 485 Dialog - Using extracted component */}
      <CMS485Dialog
        client={client}
        open={cms485DialogOpen}
        onOpenChange={setCms485DialogOpen}
        editingCms485Id={editingCms485Id}
        setEditingCms485Id={setEditingCms485Id}
        clearCms485Signatures={clearCms485Signatures}
        setClearCms485Signatures={setClearCms485Signatures}
        cms485Form={cms485Form}
        setCms485Form={setCms485Form}
        physicians={physicians}
        rnEmployees={rnEmployees}
        savingCms485={savingCms485}
        onSave={handleCreateCms485}
        clientId={id}
      />


      {/* E-Signature Dialog */}
      <Dialog open={signatureDialogOpen} onOpenChange={setSignatureDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add {signerType === 'physician' ? 'Physician' : 'Agency'} Signature</DialogTitle>
            <DialogDescription>
              Sign the CMS 485 form electronically
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Signer Name *</Label>
              <Input
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder={signerType === 'physician' ? "Dr. John Smith" : "Case Manager Name"}
              />
            </div>
            <div className="space-y-2">
              <Label>Signature</Label>
              <SignaturePad
                width={350}
                height={120}
                onSave={handleSignCms485}
                onClear={() => {}}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Visit Note Dialog */}
      <Dialog open={visitNoteDialogOpen} onOpenChange={setVisitNoteDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Caregiver Visit Note</DialogTitle>
            <DialogDescription>
              Document today&apos;s visit for {client.first_name} {client.last_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Visit Date and Times */}
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Visit Date *</Label>
                <Input
                  type="date"
                  value={newVisitNote.visit_date}
                  onChange={(e) => setNewVisitNote(prev => ({ ...prev, visit_date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Time In *</Label>
                <Input
                  type="time"
                  value={newVisitNote.time_in}
                  onChange={(e) => setNewVisitNote(prev => ({ ...prev, time_in: e.target.value }))}
                />
                <p className="text-xs text-slate-500">From EVV</p>
              </div>
              <div className="space-y-2">
                <Label>Time Out *</Label>
                <Input
                  type="time"
                  value={newVisitNote.time_out}
                  onChange={(e) => setNewVisitNote(prev => ({ ...prev, time_out: e.target.value }))}
                />
                <p className="text-xs text-slate-500">From EVV</p>
              </div>
              <div className="space-y-2">
                <Label>Duration (min)</Label>
                <Input
                  type="number"
                  value={newVisitNote.duration_minutes}
                  onChange={(e) => setNewVisitNote(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 0 }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Visit Type</Label>
                <Select 
                  value={newVisitNote.visit_type} 
                  onValueChange={(value) => setNewVisitNote(prev => ({ ...prev, visit_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {visitTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Patient Condition</Label>
                <Select 
                  value={newVisitNote.patient_condition} 
                  onValueChange={(value) => setNewVisitNote(prev => ({ ...prev, patient_condition: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stable">Stable</SelectItem>
                    <SelectItem value="improved">Improved</SelectItem>
                    <SelectItem value="declined">Declined</SelectItem>
                    <SelectItem value="unchanged">Unchanged</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Vital Signs */}
            <div className="space-y-3">
              <Label className="font-medium">Vital Signs</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Blood Pressure</Label>
                  <Input
                    placeholder="120/80"
                    value={newVisitNote.vital_signs.blood_pressure}
                    onChange={(e) => setNewVisitNote(prev => ({
                      ...prev,
                      vital_signs: { ...prev.vital_signs, blood_pressure: e.target.value }
                    }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Heart Rate (bpm)</Label>
                  <Input
                    placeholder="72"
                    value={newVisitNote.vital_signs.heart_rate}
                    onChange={(e) => setNewVisitNote(prev => ({
                      ...prev,
                      vital_signs: { ...prev.vital_signs, heart_rate: e.target.value }
                    }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Temperature (°F)</Label>
                  <Input
                    placeholder="98.6"
                    value={newVisitNote.vital_signs.temperature}
                    onChange={(e) => setNewVisitNote(prev => ({
                      ...prev,
                      vital_signs: { ...prev.vital_signs, temperature: e.target.value }
                    }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">O2 Saturation (%)</Label>
                  <Input
                    placeholder="98"
                    value={newVisitNote.vital_signs.oxygen_saturation}
                    onChange={(e) => setNewVisitNote(prev => ({
                      ...prev,
                      vital_signs: { ...prev.vital_signs, oxygen_saturation: e.target.value }
                    }))}
                  />
                </div>
              </div>
            </div>

            {/* Pain Level */}
            <div className="space-y-2">
              <Label>Pain Level (0-10)</Label>
              <div className="flex gap-1">
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((level) => (
                  <button
                    key={level}
                    type="button"
                    className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                      newVisitNote.pain_level === level
                        ? level <= 3 ? 'bg-emerald-500 text-white' 
                          : level <= 6 ? 'bg-amber-500 text-white'
                          : 'bg-red-500 text-white'
                        : 'bg-slate-100 hover:bg-slate-200'
                    }`}
                    onClick={() => setNewVisitNote(prev => ({ ...prev, pain_level: level }))}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>

            {/* Services Provided */}
            <div className="space-y-3">
              <Label className="font-medium">Services Provided</Label>
              <div className="flex flex-wrap gap-2">
                {servicesOptions.map((service) => (
                  <label key={service} className="flex items-center gap-2 px-3 py-1.5 border rounded-lg cursor-pointer hover:bg-slate-50">
                    <Checkbox
                      checked={newVisitNote.services_provided.includes(service)}
                      onCheckedChange={(checked) => {
                        setNewVisitNote(prev => ({
                          ...prev,
                          services_provided: checked
                            ? [...prev.services_provided, service]
                            : prev.services_provided.filter(s => s !== service)
                        }));
                      }}
                    />
                    <span className="text-sm">{service}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* MOTs/Observations */}
            <div className="space-y-2">
              <Label>MOTs/Observations</Label>
              <Textarea
                value={newVisitNote.mots_observations}
                onChange={(e) => setNewVisitNote(prev => ({ ...prev, mots_observations: e.target.value }))}
                placeholder="Document observations, interventions, and patient response..."
                rows={4}
              />
            </div>

            {/* Follow Up */}
            <div className="p-4 bg-slate-50 rounded-lg space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={newVisitNote.follow_up_needed}
                  onCheckedChange={(checked) => setNewVisitNote(prev => ({ ...prev, follow_up_needed: checked }))}
                />
                <Label>Follow-up Required</Label>
              </div>
              {newVisitNote.follow_up_needed && (
                <Textarea
                  value={newVisitNote.follow_up_notes}
                  onChange={(e) => setNewVisitNote(prev => ({ ...prev, follow_up_notes: e.target.value }))}
                  placeholder="Describe follow-up needs..."
                  rows={2}
                />
              )}
            </div>

            {/* Signatures */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Caregiver Signature *</Label>
                <p className="text-xs text-slate-500">Sign to confirm services provided</p>
                <SignaturePad
                  width={280}
                  height={80}
                  onSave={(sig) => setNewVisitNote(prev => ({ ...prev, caregiver_signature: sig }))}
                  onClear={() => setNewVisitNote(prev => ({ ...prev, caregiver_signature: null }))}
                />
                {newVisitNote.caregiver_signature && (
                  <p className="text-xs text-emerald-600">✓ Caregiver signature captured</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Client Signature *</Label>
                <p className="text-xs text-slate-500">Client confirms services received</p>
                <SignaturePad
                  width={280}
                  height={80}
                  onSave={(sig) => setNewVisitNote(prev => ({ ...prev, client_signature: sig }))}
                  onClear={() => setNewVisitNote(prev => ({ ...prev, client_signature: null }))}
                />
                {newVisitNote.client_signature && (
                  <p className="text-xs text-emerald-600">✓ Client signature captured</p>
                )}
              </div>
            </div>
            <p className="text-xs text-slate-500 text-center">
              RN signature is collected on the Weekly Master PCW Sheet
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVisitNoteDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateVisitNote} disabled={savingVisitNote}>
              {savingVisitNote ? "Saving..." : (newVisitNote.caregiver_signature || newVisitNote.client_signature) ? "Save & Sign" : "Save Draft"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Care Plan POC Dialog */}
      <Dialog open={carePlanPOCDialogOpen} onOpenChange={(open) => { if (!open) { setEditingPocId(null); } setCarePlanPOCDialogOpen(open); }}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPocId ? "Edit" : "Create"} Plan of Care - PCW/Caregiver</DialogTitle>
            <DialogDescription>
              {editingPocId ? "Update" : "Create"} a comprehensive care plan for {client?.first_name} {client?.last_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Client Info Section */}
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-semibold text-slate-700 mb-3">Client Information</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Name: <span className="text-slate-800 font-medium">{client?.first_name} {client?.last_name}</span></p>
                  <p className="text-slate-500">DOB: <span className="text-slate-800">{client?.dob || 'N/A'}</span></p>
                  <p className="text-slate-500">Phone: <span className="text-slate-800">{client?.phone || 'N/A'}</span></p>
                </div>
                <div>
                  <p className="text-slate-500">Address: <span className="text-slate-800">{client?.address}, {client?.city}, {client?.state} {client?.zip_code}</span></p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-3">
                <div className="space-y-1">
                  <Label className="text-xs">Preferred Name</Label>
                  <Input
                    placeholder="Nickname"
                    value={newCarePlanPOC.preferred_name}
                    onChange={(e) => setNewCarePlanPOC(prev => ({ ...prev, preferred_name: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Ability to Sign</Label>
                  <Select
                    value={newCarePlanPOC.ability_to_sign}
                    onValueChange={(val) => setNewCarePlanPOC(prev => ({ ...prev, ability_to_sign: val }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Yes">Yes</SelectItem>
                      <SelectItem value="No">No</SelectItem>
                      <SelectItem value="Limited">Limited</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Pets</Label>
                  <Input
                    placeholder="e.g., 1 dog"
                    value={newCarePlanPOC.pets}
                    onChange={(e) => setNewCarePlanPOC(prev => ({ ...prev, pets: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            {/* Emergency Contact Section */}
            <div className="p-4 border border-slate-200 rounded-lg">
              <h4 className="font-semibold text-slate-700 mb-3">Emergency Contact</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Contact Name</Label>
                  <Input
                    value={newCarePlanPOC.emergency_contact_name}
                    onChange={(e) => setNewCarePlanPOC(prev => ({ ...prev, emergency_contact_name: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Relationship</Label>
                  <Input
                    value={newCarePlanPOC.emergency_contact_relationship}
                    onChange={(e) => setNewCarePlanPOC(prev => ({ ...prev, emergency_contact_relationship: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Phone</Label>
                  <Input
                    value={newCarePlanPOC.emergency_contact_phone}
                    onChange={(e) => setNewCarePlanPOC(prev => ({ ...prev, emergency_contact_phone: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Alt. Phone</Label>
                  <Input
                    value={newCarePlanPOC.emergency_contact_alt_phone}
                    onChange={(e) => setNewCarePlanPOC(prev => ({ ...prev, emergency_contact_alt_phone: e.target.value }))}
                  />
                </div>
              </div>
              <div className="mt-3 space-y-1">
                <Label className="text-xs">Family Member(s) Support</Label>
                <Input
                  placeholder="List family members available to help"
                  value={newCarePlanPOC.family_support}
                  onChange={(e) => setNewCarePlanPOC(prev => ({ ...prev, family_support: e.target.value }))}
                />
              </div>
            </div>

            {/* Medical Information Section */}
            <div className="p-4 border border-slate-200 rounded-lg">
              <h4 className="font-semibold text-slate-700 mb-3">Medical Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Chronic Health Problems</Label>
                  <Textarea
                    rows={2}
                    value={newCarePlanPOC.chronic_health_problems}
                    onChange={(e) => setNewCarePlanPOC(prev => ({ ...prev, chronic_health_problems: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Sleep Habits</Label>
                  <Input
                    value={newCarePlanPOC.sleep_habits}
                    onChange={(e) => setNewCarePlanPOC(prev => ({ ...prev, sleep_habits: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Precautions</Label>
                  <Textarea
                    rows={2}
                    value={newCarePlanPOC.precautions}
                    onChange={(e) => setNewCarePlanPOC(prev => ({ ...prev, precautions: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">DME (Durable Medical Equipment)</Label>
                  <Input
                    value={newCarePlanPOC.dme}
                    onChange={(e) => setNewCarePlanPOC(prev => ({ ...prev, dme: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Hearing/Vision/Speech</Label>
                  <Input
                    value={newCarePlanPOC.hearing_vision_speech}
                    onChange={(e) => setNewCarePlanPOC(prev => ({ ...prev, hearing_vision_speech: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Mental Status</Label>
                  <Input
                    value={newCarePlanPOC.mental_status}
                    onChange={(e) => setNewCarePlanPOC(prev => ({ ...prev, mental_status: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Allergies</Label>
                  <Input
                    value={newCarePlanPOC.allergies}
                    onChange={(e) => setNewCarePlanPOC(prev => ({ ...prev, allergies: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Code Status</Label>
                  <Select
                    value={newCarePlanPOC.code_status}
                    onValueChange={(val) => setNewCarePlanPOC(prev => ({ ...prev, code_status: val }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Full Code">Full Code</SelectItem>
                      <SelectItem value="DNR">DNR</SelectItem>
                      <SelectItem value="DNI">DNI</SelectItem>
                      <SelectItem value="DNR/DNI">DNR/DNI</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Schedule Section */}
            <div className="p-4 border border-slate-200 rounded-lg">
              <h4 className="font-semibold text-slate-700 mb-3">Schedule</h4>
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Hours/Week</Label>
                  <Input
                    type="number"
                    value={newCarePlanPOC.hours_per_week}
                    onChange={(e) => setNewCarePlanPOC(prev => ({ ...prev, hours_per_week: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Time of Visits</Label>
                  <Input
                    placeholder="e.g., 9AM-1PM"
                    value={newCarePlanPOC.time_of_visits}
                    onChange={(e) => setNewCarePlanPOC(prev => ({ ...prev, time_of_visits: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Start of Care (SOC)</Label>
                  <Input
                    type="date"
                    value={newCarePlanPOC.soc_date}
                    onChange={(e) => setNewCarePlanPOC(prev => ({ ...prev, soc_date: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Days of Week</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {daysOfWeek.map((day) => (
                      <Button
                        key={day}
                        type="button"
                        size="sm"
                        variant={newCarePlanPOC.days_of_week.includes(day) ? "default" : "outline"}
                        className="h-7 w-8 text-xs p-0"
                        onClick={() => toggleDayOfWeek(day)}
                      >
                        {day}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Activities of Daily Living (ADL) Section */}
            <div className="p-4 border border-slate-200 rounded-lg">
              <h4 className="font-semibold text-slate-700 mb-1">Activities of Daily Living</h4>
              <p className="text-xs text-slate-500 mb-3">Specify assistance type, frequency, and notes for each day</p>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="px-2 py-2 text-left text-xs font-medium text-slate-600">ADL</th>
                      <th className="px-2 py-2 text-left text-xs font-medium text-slate-600 w-32">Assistance Type</th>
                      <th className="px-2 py-2 text-left text-xs font-medium text-slate-600 w-24">Frequency</th>
                      {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(day => (
                        <th key={day} className="px-1 py-2 text-center text-xs font-medium text-slate-600 w-14">{day}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { key: "adl_bathing", label: "Bathing" },
                      { key: "adl_dressing", label: "Dressing" },
                      { key: "adl_grooming", label: "Grooming" },
                      { key: "adl_meals", label: "Meals" },
                      { key: "adl_mobility", label: "Mobility" },
                      { key: "adl_toileting", label: "Toileting" },
                      { key: "adl_transfers", label: "Transfers" },
                      { key: "adl_med_reminder", label: "Med Reminder" },
                      { key: "adl_cleaning", label: "Cleaning" },
                      { key: "adl_laundry", label: "Laundry" },
                      { key: "adl_other", label: "Other" },
                    ].map((adl) => (
                      <tr key={adl.key} className="border-b border-slate-100">
                        <td className="px-2 py-2 text-xs font-medium text-slate-700">{adl.label}</td>
                        <td className="px-2 py-1">
                          <Select
                            value={newCarePlanPOC[adl.key]?.assistance_type || ""}
                            onValueChange={(val) => updateADL(adl.key, "assistance_type", val)}
                          >
                            <SelectTrigger className="h-7 text-xs">
                              <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent>
                              {(adl.key === "adl_meals" ? mealsAssistanceTypes : assistanceTypes).map(type => (
                                <SelectItem key={type} value={type} className="text-xs">{type}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-1">
                          <Select
                            value={newCarePlanPOC[adl.key]?.frequency || ""}
                            onValueChange={(val) => updateADL(adl.key, "frequency", val)}
                          >
                            <SelectTrigger className="h-7 text-xs">
                              <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="AM" className="text-xs">AM</SelectItem>
                              <SelectItem value="PM" className="text-xs">PM</SelectItem>
                              <SelectItem value="Both" className="text-xs">Both</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        {["mon", "tue", "wed", "thu", "fri", "sat", "sun"].map(day => (
                          <td key={day} className="px-1 py-1">
                            <Input
                              className="h-7 text-xs text-center p-1"
                              placeholder="✓"
                              value={newCarePlanPOC[adl.key]?.[day] || ""}
                              onChange={(e) => updateADL(adl.key, day, e.target.value)}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Other ADL description */}
              <div className="mt-3 space-y-1">
                <Label className="text-xs">Other ADL Description (if applicable)</Label>
                <Input
                  placeholder="e.g., Shopping assistance, Companionship"
                  value={newCarePlanPOC.other_adl_description}
                  onChange={(e) => setNewCarePlanPOC(prev => ({ ...prev, other_adl_description: e.target.value }))}
                />
              </div>
            </div>

            {/* Parameters and Interventions Section */}
            <div className="p-4 border border-slate-200 rounded-lg">
              <h4 className="font-semibold text-slate-700 mb-1">Parameters and Interventions</h4>
              <p className="text-xs text-slate-500 mb-3">Document specific parameters to monitor and interventions to perform</p>
              
              <div className="space-y-4">
                {/* Parameters */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Parameters</Label>
                  <p className="text-xs text-slate-500">Vital signs, measurements, or conditions to monitor</p>
                  <Textarea
                    placeholder="e.g., Monitor blood pressure daily, Check blood sugar before meals, Weigh patient weekly..."
                    value={newCarePlanPOC.parameters || ""}
                    onChange={(e) => setNewCarePlanPOC(prev => ({ ...prev, parameters: e.target.value }))}
                    rows={4}
                    className="text-sm"
                  />
                </div>
                
                {/* Interventions */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Interventions</Label>
                  <p className="text-xs text-slate-500">Actions and care tasks to be performed</p>
                  <Textarea
                    placeholder="e.g., Assist with medication reminders, Perform range of motion exercises, Apply compression stockings..."
                    value={newCarePlanPOC.interventions || ""}
                    onChange={(e) => setNewCarePlanPOC(prev => ({ ...prev, interventions: e.target.value }))}
                    rows={4}
                    className="text-sm"
                  />
                </div>

                {/* Goals */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Goals</Label>
                  <p className="text-xs text-slate-500">Expected outcomes and objectives</p>
                  <Textarea
                    placeholder="e.g., Patient will maintain stable blood pressure, Patient will ambulate independently with walker..."
                    value={newCarePlanPOC.goals || ""}
                    onChange={(e) => setNewCarePlanPOC(prev => ({ ...prev, goals: e.target.value }))}
                    rows={4}
                    className="text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCarePlanPOCDialogOpen(false); setEditingPocId(null); }}>
              Cancel
            </Button>
            <Button onClick={handleCreateCarePlanPOC} disabled={savingCarePlanPOC}>
              {savingCarePlanPOC ? "Saving..." : editingPocId ? "Update Care Plan" : "Create Care Plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* POC Signature Dialog */}
      <Dialog open={pocSignatureDialogOpen} onOpenChange={setPocSignatureDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {pocSignerType === "rn" ? "RN Signature" : pocSignerType === "client" ? "Client/Representative Signature" : "PCW/Caregiver Signature"}
            </DialogTitle>
            <DialogDescription>
              Sign the Plan of Care document
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="poc-signer-name">Signer Name *</Label>
              <Input
                id="poc-signer-name"
                placeholder="Enter full name"
                value={pocSignerName}
                onChange={(e) => setPocSignerName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Signature</Label>
              <SignaturePad
                onSave={handleSignCarePlanPOC}
                width={400}
                height={150}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add RN Review Dialog */}
      <Dialog open={rnReviewDialogOpen} onOpenChange={setRnReviewDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add RN Review/Update</DialogTitle>
            <DialogDescription>
              Document your review of this Plan of Care with your signature
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500">Review Date</p>
              <p className="text-sm font-medium text-slate-800">{new Date().toLocaleDateString()}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rn-review-notes">Review Notes *</Label>
              <Textarea
                id="rn-review-notes"
                placeholder="Enter your review notes, observations, and any updates to the care plan..."
                value={rnReviewNotes}
                onChange={(e) => setRnReviewNotes(e.target.value)}
                rows={4}
              />
            </div>
            <div className="space-y-2">
              <Label>RN Signature *</Label>
              <div className="border border-slate-200 rounded-lg p-2 bg-white">
                <SignaturePad
                  onSave={(sig) => setRnReviewSignature(sig)}
                  onClear={() => setRnReviewSignature(null)}
                  width={380}
                  height={100}
                />
              </div>
              {rnReviewSignature && (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <Check className="h-3 w-3" /> Signature captured
                </p>
              )}
            </div>
            <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-700">
                <strong>Note:</strong> Your signature and this review will be permanently added to the care plan and included in the PDF.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRnReviewDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddRnReview} 
              disabled={savingRnReview || !rnReviewNotes.trim() || !rnReviewSignature}
            >
              {savingRnReview ? "Saving..." : "Sign & Add Review"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View RN Reviews Dialog */}
      <Dialog open={viewReviewsDialogOpen} onOpenChange={setViewReviewsDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>RN Review History</DialogTitle>
            <DialogDescription>
              All reviews for this Plan of Care
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {viewingPoc?.rn_reviews?.length > 0 ? (
              <div className="space-y-3">
                {viewingPoc.rn_reviews.map((review, idx) => (
                  <div
                    key={idx}
                    className="p-3 border border-slate-200 rounded-lg"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded">
                        Review #{idx + 1}
                      </span>
                      <span className="text-xs text-slate-500">
                        {review.date ? new Date(review.date).toLocaleDateString() : 'Date not recorded'}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap mb-2">
                      {review.notes}
                    </p>
                    {review.signature && (
                      <div className="mt-2 p-2 bg-slate-50 rounded border border-slate-100">
                        <p className="text-xs text-slate-500 mb-1">Signature:</p>
                        <img 
                          src={review.signature} 
                          alt="RN Signature" 
                          className="max-h-12 border border-slate-200 rounded bg-white"
                        />
                      </div>
                    )}
                    {(review.reviewer_name || review.reviewer) && (
                      <p className="text-xs text-slate-400 mt-2">
                        Reviewed by: {review.reviewer_name || review.reviewer}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <p className="text-sm">No reviews recorded yet</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewReviewsDialogOpen(false)}>
              Close
            </Button>
            <Button onClick={() => {
              setViewReviewsDialogOpen(false);
              openRnReviewDialog(viewingPoc?.id);
            }}>
              <Plus className="w-4 h-4 mr-1" />
              Add New Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* eFax Dialog - Using extracted component */}
      <EfaxDialog
        open={efaxDialogOpen}
        onOpenChange={setEfaxDialogOpen}
        efaxData={efaxData}
        setEfaxData={setEfaxData}
        sendingEfax={sendingEfax}
        onSendEfax={handleSendEfax}
      />

      {/* Physician Sign Link Dialog - Using extracted component */}
      <SignLinkDialog
        open={signLinkDialogOpen}
        onOpenChange={handleCloseSignLinkDialog}
        signLinkData={signLinkData}
        setSignLinkData={setSignLinkData}
        creatingSignLink={creatingSignLink}
        onCreateSignLink={handleCreateSignLink}
        createdSignLinkUrl={createdSignLinkUrl}
        createdSignLinkMeta={createdSignLinkMeta}
        onResendEmail={handleResendSignLinkEmail}
        onCopySignLink={handleCopySignLink}
      />

      {/* Edit MD Order Dialog - Using extracted component */}
      <EditMDOrderDialog
        open={editMdOrderDialogOpen}
        onOpenChange={setEditMdOrderDialogOpen}
        editingMdOrder={editingMdOrder}
        setEditingMdOrder={setEditingMdOrder}
        onSave={handleSaveMdOrder}
      />

      {/* View MD Order Dialog - Using extracted component */}
      <ViewMDOrderDialog
        open={viewMdOrderDialogOpen}
        onOpenChange={setViewMdOrderDialogOpen}
        viewingMdOrder={viewingMdOrder}
        onEdit={openEditMdOrder}
      />

      {/* Supervisory Visit Dialog - Using extracted component */}
      <SupervisoryVisitDialog
        open={supervisoryDialogOpen}
        onOpenChange={setSupervisoryDialogOpen}
        newSupervisoryVisit={newSupervisoryVisit}
        setNewSupervisoryVisit={setNewSupervisoryVisit}
        supervisoryFormFile={supervisoryFormFile}
        setSupervisoryFormFile={setSupervisoryFormFile}
        handleVisitDateChange={handleVisitDateChange}
        userName={user?.name}
        saving={savingSupervisory}
        onSave={handleCreateSupervisoryVisit}
      />

      {/* Quarterly Meeting Dialog - Using extracted component */}
      <QuarterlyMeetingDialog
        open={quarterlyDialogOpen}
        onOpenChange={setQuarterlyDialogOpen}
        clientName={`${client?.first_name} ${client?.last_name}`}
        newQuarterlyMeeting={newQuarterlyMeeting}
        setNewQuarterlyMeeting={setNewQuarterlyMeeting}
        saving={savingQuarterly}
        onSave={handleCreateQuarterlyMeeting}
      />

      {/* Add Note Dialog - Using extracted component */}
      <NotesDialog
        open={notesDialogOpen}
        onOpenChange={setNotesDialogOpen}
        noteType={noteType}
        newNote={newNote}
        setNewNote={setNewNote}
        newNoteDate={newNoteDate}
        setNewNoteDate={setNewNoteDate}
        saving={savingNote}
        editingNoteId={editingNoteId}
        onSave={handleAddNote}
      />


      {/* Authorization Dialog - Using extracted component */}
      <ServiceOrderDialog
        open={serviceOrderDialogOpen}
        onOpenChange={setServiceOrderDialogOpen}
        clientName={`${client?.first_name} ${client?.last_name}`}
        editingServiceOrderId={editingServiceOrderId}
        newServiceOrder={newServiceOrder}
        setNewServiceOrder={setNewServiceOrder}
        masterServiceCodes={masterServiceCodes}
        employees={employees}
        saving={savingServiceOrder}
        onSave={handleCreateServiceOrder}
        onResetForm={resetServiceOrderForm}
        calculateServiceOrderTotals={calculateServiceOrderTotals}
        handleServiceCodeChange={handleServiceCodeChange}
      />


      {/* Notes */}
      {client.notes && (
        <div className="form-section mt-6">
          <h3 className="form-section-title">Notes</h3>
          <p className="text-sm text-slate-600 whitespace-pre-wrap">{client.notes}</p>
        </div>
      )}

      {/* ROI Launch Dialog */}
      <ROILaunchDialog
        open={roiDialogOpen}
        onOpenChange={setRoiDialogOpen}
        clientId={client.id}
        clientName={`${client.first_name} ${client.last_name}`}
        clientEmail={client.email}
        clientPhone={client.phone}
      />
    </Layout>
  );
}
